import * as THREE from "three";
import { assetUrl } from "../assetVer.js"; // ?v=<build> cache-buster so re-rendered scene textures refresh on deploy
import { buildVillage, wallSeg, atmMachine, aptWallTex, aptConcreteTex, type AptUnitPlace } from "./villageScene.js";
import { buildKaponTerrain, kaponNorthBoundZ } from "./kaponTerrain.js";
import { furnitureMesh } from "./furniture.js";
import { ThreadsHologram } from "./threadsHologram.js";
import { KAPON_TERRAIN, cookingPotAnchor, drinkDispenserAnchor, fridgeAnchor, storeChestAnchor, sewingMachineAnchor, RESTAURANT_CENTERS, regionOfZone, type Region } from "@ghostland/shared";

/**
 * Winston's — a lone old tavern in the desert at night, now an ACTUAL little 3D building
 * (textured walls + pitched roof + an open doorway) on a lamp-lit flagstone plaza, plus a
 * separate warm INTERIOR room you can walk into. Pure scene content (geometry + generated
 * pixel textures), decoupled from the networked controller.
 *
 * Exterior and interior are two groups in ONE scene at different world locations; the
 * controller toggles which is visible (and swaps fog/bounds) as the local player crosses
 * between the plaza and the interior coordinate zone. Because every player's position is
 * already synced, anyone who walks into the interior zone is simply rendered there too —
 * the interior is multiplayer for free.
 */

/** World units per server pixel: 32px ≈ 1 unit, so SPAWN(320,240) → ~(10, 7.5). */
export const U = 1 / 32;
const PX = 1 / U; // server px per world unit (32)
export const SCENE = "/assets/scene";

export interface Blocker { x0: number; x1: number; y0: number; y1: number }
/** Walkable region in server px; the controller clamps the player inside it. */
export interface WalkBounds { x0: number; y0: number; x1: number; y1: number }

/** Everything the controller needs to run + swap between the plaza and the interior. */
/** The walkable areas of the world; the controller moves the player between them via portals. */
export type ZoneId =
  | "plaza" | "tavern" | "village" | "threads" | "casino" | "bank" | "citytavern" | "kapon" | "food" | "ghosts" | "furni" | "tackle" | "smith"
  // the apartment tenements (the first block + the ANNEX): each has a ground-floor lobby, one
  // stair-hall per upper floor, and 4 apartment HOMES
  | "aptlobby" | "apthall2" | "apthall3" | "apt2a" | "apt2b" | "apt3a" | "apt3b"
  | "anxlobby" | "anxhall2" | "anxhall3" | "anx2a" | "anx2b" | "anx3a" | "anx3b";

/** The apartment HOME zones — real rooms physically inside the tenements, sharing the VILLAGE's
 *  render world (same group/fog; zoneOf reports players inside them as "village"). A local player's
 *  zone still reads apt2a…/anx2a… (set by the door portal) for bounds/locks/keys. */
const APT_UNIT_ZONES: readonly ZoneId[] = ["apt2a", "apt2b", "apt3a", "apt3b", "anx2a", "anx2b", "anx3a", "anx3b"];
export function isAptUnitZone(z: string): boolean { return (APT_UNIT_ZONES as readonly string[]).includes(z); }

/** A travel point: standing on `trigger` (in `zone`) and pressing E warps to `to`@`spawn`. */
export interface Portal {
  zone: ZoneId;
  to: ZoneId;
  trigger: { x: number; y: number };  // server px
  spawn: { x: number; y: number };    // server px (where you arrive)
  label: string;                      // verb for the prompt, e.g. "enter", "leave the village"
  lockable?: boolean;                 // the tavern entrance respects the tavern lock
  /** Arrival storey (0/omitted = ground) — apartment homes sit at real elevation (Player.floor). */
  floor?: number;
}

export interface ZoneData {
  group: THREE.Group;
  bounds: WalkBounds;
  blockers: Blocker[];
  /** Solid meshes the trailing camera shouldn't pass through (pulls in WoW-style). */
  colliders: THREE.Object3D[];
  fog: THREE.Fog;
  bg: number;
}

export interface SceneBuild {
  /** Built zones. The spawn island (plaza + tavern) exists from boot; the village cluster and Kapon
   *  are each added lazily + independently by `ensure(zone)`, so this stays partial until visited. */
  zones: Partial<Record<ZoneId, ZoneData>>;
  portals: Portal[];
  /** Which zone a server-px point belongs to (for remote-player visibility). */
  zoneOf: (x: number, y: number) => ZoneId;
  /** Apply fog/background + group visibility for the current zone. */
  applyZone: (scene: THREE.Scene, zone: ZoneId) => void;
  /** Drive per-frame tavern door-leaf animation (`doorsOpen` ⇒ leaves swung open). */
  animate: (dt: number, doorsOpen: boolean) => void;
  /** Tavern NPC placement (world units): adventurer wander rect + bartender spot. */
  tavernNpc: { area: { x0: number; z0: number; x1: number; z1: number }; bar: { x: number; z: number } };
  /** Build the region containing `zone` (the village cluster or Kapon) + its zones/portals on first
   *  travel. Idempotent; a no-op for the always-resident spawn island (plaza/tavern). */
  ensure: (zone: ZoneId) => void;
  /** Whether the region containing `zone` is built (the spawn island is always true). */
  loaded: (zone: ZoneId) => boolean;
  /** Fully unload a region (remove + dispose its geometry/textures, drop its portals + placement refs)
   *  so `ensure()` rebuilds it on return. No-op for the always-resident spawn island. */
  disposeRegion: (region: Region) => void;
  /** Village NPC stand positions (world units) — null until the village region builds. */
  villageNpc: Record<"banker" | "casino" | "guardE" | "guardW" | "winston" | "miner" | "gardener" | "blackmarket" | "marketeer" | "vendor" | "fishmonger", { x: number; z: number; face: number }> | null;
  /** Where ambient village townsfolk wander (world units) — null until the village region builds. */
  villageNpcArea: { x0: number; z0: number; x1: number; z1: number } | null;
  /** Where Vex stands inside the THREADS store (world units) — null until the village region builds. */
  threadsNpc: { x: number; z: number; face: number } | null;
  /** Where the casino host (Roxy) stands inside the CASINO (world units) — null until the village region builds. */
  casinoNpc: { x: number; z: number; face: number } | null;
  /** The 5 blackjack stool positions (world units), indexed by seat — null until the village region builds.
   *  Seated players' billboards snap onto these. */
  casinoSeats: { x: number; z: number }[] | null;
  /** Where the poker dealer stands inside the CASINO (world units) — null until the village region builds. */
  pokerNpc: { x: number; z: number; face: number } | null;
  /** The 6 poker stool positions (world units), indexed by seat — null until the village region builds. */
  pokerSeats: { x: number; z: number }[] | null;
  /** Where Mr. Vault stands inside the BANK (world units) — null until the village region builds. */
  bankNpc: { x: number; z: number; face: number } | null;
  /** Where the food vendor stands inside the FOOD STORE (world units) — null until the village region builds. */
  foodNpc: { x: number; z: number; face: number } | null;
  /** Where the Ghost's vendor stands inside the GHOST'S restaurant (world units) — null until the village region builds. */
  ghostsNpc: { x: number; z: number; face: number } | null;
  /** Where the clerk stands inside OKKA FURNITURE (world units) — null until the village region builds. */
  furniNpc: { x: number; z: number; face: number } | null;
  /** Where the Old Timer stands inside OLD TIMER'S BAITS — null until the village region builds. */
  tackleNpc: { x: number; z: number; face: number } | null;
  /** Where Ember stands inside the BLACKSMITH shop — null until the village region builds. */
  smithNpc: { x: number; z: number; face: number } | null;
  /** The tackle shop's 8-direction bucket sprite ticker (frame follows the camera yaw). */
  tackleBucket: { tick: (camYaw: number) => void; board: { x: number; y: number } } | null;
  /** The furniture store's counter REGISTER (server px + mesh for click picking) — the owner collects
   *  the store's takings here. Null until the village region builds. */
  furniRegister: { x: number; y: number; obj: THREE.Object3D } | null;
  /** Per-restaurant interactable kitchen props (server px + mesh for click picking), keyed by building id
   *  ("food", "ghosts", …) so each restaurant's own pot/fridge/dispenser are clickable — null until the village region builds. */
  restaurantProps: Record<string, {
    fridge: { x: number; y: number; obj: THREE.Object3D };
    pot: { x: number; y: number; obj: THREE.Object3D };
    dispenser: { x: number; y: number; obj: THREE.Object3D };
  }> | null;
  /** The city WINSTON'S tavern NPC placement (world units): adventurer wander rect + bar spot. */
  cityTavernNpc: { area: { x0: number; z0: number; x1: number; z1: number }; bar: { x: number; z: number } } | null;
  /** ATM machine positions (server px) in the city — null until the village region builds. */
  atms: { x: number; y: number }[] | null;
  /** The warehouse Leather Processing Machine (server px + mesh for click picking) — null until the village region builds. */
  leatherMachine: { x: number; y: number; obj: THREE.Object3D } | null;
  /** The warehouse Crafting Table (server px + mesh for click picking) — null until the village region builds. */
  craftingTable: { x: number; y: number; obj: THREE.Object3D } | null;
  /** The bank's wealth-rankboard interaction point (server px) — null until the village region builds. */
  rankboard: { x: number; y: number } | null;
  /** The bank's vault-storage interaction point (server px) — null until the village region builds. */
  storage: { x: number; y: number } | null;
  /** The apartment-lobby ELEVATORS (one per tenement block): the lobby zone each serves, its call
   *  point (server px) + the floors it reaches (label → zone + arrival spawn). Stand at the doors
   *  + E → the floor picker. Null until the village region builds. */
  aptElevators: { zone: ZoneId; x: number; y: number; floors: { label: string; zone: ZoneId; spawn: { x: number; y: number } }[] }[] | null;
  /** The apartment HOMES physically inside the tenements (their zones share the village world):
   *  occupancy bounds (server px, 2D — combine with Player.floor), each home's ceiling light (world3d
   *  turns it ON while someone is home), its window curtain mesh (mirrors the synced
   *  Building.curtains), and the stand-here anchor for the E curtain toggle. Null until built. */
  aptUnits: { zone: ZoneId; floor: number; bounds: WalkBounds; light: THREE.SpotLight; curtain: THREE.Mesh; windowAnchor: { x: number; y: number } }[] | null;
  /** The tenements' whole footprints (server px). A player who relogs INSIDE one (they logged out at
   *  home — the floor isn't persisted) is snapped to that block's street door instead. Null until built. */
  aptFootprints: WalkBounds[] | null;
  /** Teleport destinations (server px): the city (Ghost Land scroll) + the spawn island. */
  citySpawn: { x: number; y: number };
  spawnIsland: { x: number; y: number };
  /** Where you arrive in Kapon North Way (server px) — known from boot (fixed terrain geometry). */
  kaponSpawn: { x: number; y: number } | null;
}

/** Free a material + its textures (each zone owns its own textures, so this never touches another
 *  region's GPU resources — TextureLoader returns a fresh Texture per load). */
function disposeMaterial(m: THREE.Material): void {
  for (const k of ["map", "normalMap", "roughnessMap", "metalnessMap", "emissiveMap", "alphaMap", "aoMap", "bumpMap", "lightMap"] as const) {
    const t = (m as unknown as Record<string, { dispose?: () => void } | null>)[k];
    if (t && typeof t.dispose === "function") t.dispose();
  }
  m.dispose();
}

/** Recursively dispose every geometry + material + texture under an Object3D (meshes AND sprites), so
 *  unloading a region actually frees its VRAM instead of just hiding it. */
export function disposeObject3D(obj: THREE.Object3D): void {
  obj.traverse((o) => {
    const node = o as THREE.Object3D & { geometry?: { dispose?: () => void }; material?: THREE.Material | THREE.Material[] };
    if (node.geometry && typeof node.geometry.dispose === "function") node.geometry.dispose();
    const mat = node.material;
    if (Array.isArray(mat)) mat.forEach(disposeMaterial);
    else if (mat && typeof (mat as THREE.Material).dispose === "function") disposeMaterial(mat as THREE.Material);
  });
}

/** A pixel-art texture with nearest filtering + sRGB (no wrap). */
export function loadPixelTexture(path: string): THREE.Texture {
  const tex = new THREE.TextureLoader().load(assetUrl(path));
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** A repeating (tiled) pixel texture for walls/floors. */
function tiledTexture(path: string, repeatX: number, repeatY: number): THREE.Texture {
  const tex = loadPixelTexture(path);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(repeatX, repeatY);
  return tex;
}

/** A text label as a camera-facing sprite (names above characters). */
export function makeLabel(text: string, color = "#e8d8b0"): THREE.Sprite {
  const pad = 8, fs = 28;
  const measure = document.createElement("canvas").getContext("2d")!;
  measure.font = `${fs}px monospace`;
  const w = Math.ceil(measure.measureText(text).width) + pad * 2;
  const h = fs + pad * 2;
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.font = `${fs}px monospace`;
  ctx.textBaseline = "middle";
  ctx.fillStyle = color;
  ctx.fillText(text, pad, h / 2);
  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set((w / h) * 0.5, 0.5, 1);
  return sprite;
}

/** A flat ground quad tiled with a repeating pixel texture (`tileWorld` units/tile). */
function groundRect(path: string, cx: number, cz: number, w: number, d: number, y: number, tileWorld: number): THREE.Mesh {
  const tex = tiledTexture(path, w / tileWorld, d / tileWorld);
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, d), new THREE.MeshStandardMaterial({ map: tex }));
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.set(cx, y, cz);
  return mesh;
}

/** A clean billboard lamp-post sprite (no baked glow) anchored at its base + a warm light. */
function addLamp(group: THREE.Group, x: number, z: number): void {
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: loadPixelTexture(`${SCENE}/props/lamp.png`), transparent: true, alphaTest: 0.4,
  }));
  sprite.center.set(0.5, 0.02); // stand on the ground
  const H = 3.4; // taller than before; clearly above the 2.2-unit player
  sprite.scale.set(H * 0.16, H, 1); // lamp art is a thin tall post (~26x168)
  sprite.position.set(x, 0, z);
  group.add(sprite);
  // The actual glow is a light (the lamp art itself carries no glow now).
  const glow = new THREE.PointLight(0xffc98c, 16, 9, 2.0);
  glow.position.set(x, 2.6, z);
  group.add(glow);
}

/** A thin textured box wall. */
function wall(tex: THREE.Texture, cx: number, cy: number, cz: number, w: number, h: number, d: number): THREE.Mesh {
  const m = new THREE.MeshStandardMaterial({ map: tex });
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
  mesh.position.set(cx, cy, cz);
  return mesh;
}

/** A self-lit (unlit) transparent decal plane — the painted pixel art IS the lighting, so
 *  warm glows read against the dark night. `tint` (<1) dims it (e.g. a moonlit roof). */
function selfLitMesh(geo: THREE.BufferGeometry, tex: THREE.Texture, opts: { transparent?: boolean; tint?: number } = {}): THREE.Mesh {
  const m = new THREE.MeshBasicMaterial({
    map: tex,
    transparent: opts.transparent ?? false,
    alphaTest: opts.transparent ? 0.5 : 0,
    side: THREE.DoubleSide,
    color: new THREE.Color(opts.tint ?? 1, opts.tint ?? 1, opts.tint ?? 1),
  });
  return new THREE.Mesh(geo, m);
}

/** What buildTavern hands back: the footprint blocker, the solid meshes the camera should
 *  collide with (so it pulls in WoW-style), and a per-frame door animator. */
interface TavernBuild {
  blockers: Blocker[];
  colliders: THREE.Object3D[];
  animate: (dt: number, doorsOpen: boolean) => void;
}

/** Build the tavern as a real seamless 3D building. The four walls share ONE
 *  horizontally-tileable wall texture (features locked to fixed horizontal bands) on a
 *  SQUARE box with INTEGER tiling, so the eave / window row / stone base line up across
 *  every corner with no seam. A hip roof of tileable shingles caps it. The entrance is a
 *  real 3D doorway: two wooden leaves (straight-on art) that swing inward when the player
 *  approaches, over a dark opening, with the flat WINSTON'S sign above. Centered at world
 *  (cx,*) with its south (front) face at z=frontZ. */
function buildTavern(group: THREE.Group, cx: number, frontZ: number): TavernBuild {
  const W = 6.0, WALL_H = 4.0;          // square footprint; door ≈ player height
  const D = W;
  const cz = frontZ - D / 2;            // box center depth
  const colliders: THREE.Object3D[] = [];

  // Walls: one tileable texture, repeated 2× horizontally (→ 2 windows/face) and 1× tall
  // (bands at fixed heights). Square box + integer tiling ⇒ corners connect seamlessly.
  const wallTex = tiledTexture(`${SCENE}/buildings/tavern_wall.png`, 2, 1);
  const walls = selfLitMesh(new THREE.BoxGeometry(W, WALL_H, D), wallTex);
  walls.position.set(cx, WALL_H / 2, cz);
  group.add(walls);
  colliders.push(walls);

  // Hip roof: a 4-sided pyramid (cone, 4 segments) rotated 45° so each slope covers a wall,
  // with a slight overhang. Dimmed shingle texture reads as a moonlit roof.
  const roofH = 2.2, overhang = 0.4;
  const R = Math.SQRT2 * (W / 2 + overhang);
  const roofTex = tiledTexture(`${SCENE}/buildings/tavern_roof.png`, 5, 2);
  const roof = selfLitMesh(new THREE.ConeGeometry(R, roofH, 4), roofTex, { tint: 0.62 });
  roof.rotation.y = Math.PI / 4;
  roof.position.set(cx, WALL_H + roofH / 2, cz);
  group.add(roof);
  colliders.push(roof);

  // ---- Entrance: a framed 3D doorway with two swinging leaves ----
  // The door FRAME is a generated texture (heavy timber posts + lintel, bold edges) with a
  // painted dark opening; the leaves sit inside that opening (measured fractions below).
  const frontFace = frontZ + 0.02;
  const FH = 2.3, FW = FH * 0.8;         // frame is 0.8 aspect; a modest door, not a giant portal
  const frame = selfLitMesh(new THREE.PlaneGeometry(FW, FH), loadPixelTexture(`${SCENE}/buildings/tavern_doorframe.png`), { transparent: true });
  frame.position.set(cx, FH / 2, frontFace);
  group.add(frame);

  // Opening rectangle within the frame (fractions from post-frame.mjs: x .222–.777, y .167–.881 from top).
  const Wo = 0.555 * FW;                 // opening width
  const Ho = (0.881 - 0.167) * FH;       // opening height
  const oCy = FH * (1 - (0.167 + 0.881) / 2); // opening centre height above ground

  // two leaves, each hinged at its OUTER edge, swinging OUTWARD (+z, out of the building)
  const half = Wo / 2;
  const makeLeaf = (sign: -1 | 1): THREE.Group => {
    const pivot = new THREE.Group();
    pivot.position.set(cx + sign * half, oCy, frontFace + 0.05);
    const tex = loadPixelTexture(`${SCENE}/buildings/tavern_doorleaf.png`);
    if (sign < 0) { tex.wrapS = THREE.RepeatWrapping; tex.repeat.x = -1; } // mirror the left leaf
    const leaf = selfLitMesh(new THREE.PlaneGeometry(half, Ho), tex);
    leaf.position.x = -sign * half / 2; // span inward from the hinge to the centre
    pivot.add(leaf);
    group.add(pivot);
    return pivot;
  };
  const leftPivot = makeLeaf(-1);
  const rightPivot = makeLeaf(1);

  // Flat, straight-on WINSTON'S sign mounted just above the doorframe (true 159×80 aspect).
  const sx = 2.0, sy = sx / (159 / 80);
  const sign = selfLitMesh(new THREE.PlaneGeometry(sx, sy), loadPixelTexture(`${SCENE}/buildings/tavern_sign.png`), { transparent: true });
  sign.position.set(cx, FH + sy / 2 - 0.2, frontFace + 0.06);
  group.add(sign);

  // warm pool of light spilling from the doorway onto the porch
  const porch = new THREE.PointLight(0xffb265, 9, 8, 2.0);
  porch.position.set(cx, 1.6, frontZ + 0.8);
  group.add(porch);

  // Door animation: ease the leaves between closed (0) and open (~108° OUTWARD, +z).
  const OPEN = 1.9;
  let cur = 0;
  const animate = (dt: number, doorsOpen: boolean): void => {
    cur += ((doorsOpen ? OPEN : 0) - cur) * Math.min(1, dt * 6);
    leftPivot.rotation.y = -cur;  // +x free edge swings toward +z (outward)
    rightPivot.rotation.y = cur;  // mirror
  };

  return {
    // Footprint blocker (server px): the whole building is solid; you enter via the door (E).
    blockers: [{ x0: (cx - W / 2) * PX, x1: (cx + W / 2) * PX, y0: (cz - D / 2) * PX, y1: (cz + D / 2) * PX }],
    colliders,
    animate,
  };
}

/** Build the warm tavern interior room into `group`, centered at world (cx,cz). Returns
 *  the room's walk bounds, wall blockers, and door/spawn points (all server px). */
function buildInterior(group: THREE.Group, cx: number, cz: number): {
  bounds: WalkBounds; blockers: Blocker[]; colliders: THREE.Object3D[]; door: { x: number; y: number }; spawn: { x: number; y: number };
  npcArea: { x0: number; z0: number; x1: number; z1: number }; barSpot: { x: number; z: number };
} {
  const RW = 8, RD = 6, WH = 3.0, T = 0.3;
  const southZ = cz + RD / 2, northZ = cz - RD / 2;
  const wtex = () => tiledTexture(`${SCENE}/interior/wall.png`, RW / 1.6, WH / 1.6);
  const stex = () => tiledTexture(`${SCENE}/interior/wall.png`, RD / 1.6, WH / 1.6);
  const DW = 1.6, DH = 2.4; // exit doorway in the south wall

  group.add(groundRect(`${SCENE}/tiles/wood.png`, cx, cz, RW, RD, 0.0, 0.5)); // floor
  // ceiling (dark)
  const ceil = new THREE.Mesh(new THREE.PlaneGeometry(RW, RD), new THREE.MeshStandardMaterial({ color: 0x140d08 }));
  ceil.rotation.x = Math.PI / 2; ceil.position.set(cx, WH, cz); group.add(ceil);
  // walls: north + two sides solid; south split around the exit doorway
  const colliders: THREE.Object3D[] = [];
  const addWall = (m: THREE.Mesh): void => { group.add(m); colliders.push(m); };
  addWall(wall(wtex(), cx, WH / 2, northZ, RW, WH, T));
  addWall(wall(stex(), cx - RW / 2, WH / 2, cz, T, WH, RD));
  addWall(wall(stex(), cx + RW / 2, WH / 2, cz, T, WH, RD));
  const jambW = (RW - DW) / 2;
  addWall(wall(wtex(), cx - DW / 2 - jambW / 2, WH / 2, southZ, jambW, WH, T));
  addWall(wall(wtex(), cx + DW / 2 + jambW / 2, WH / 2, southZ, jambW, WH, T));
  addWall(wall(wtex(), cx, (DH + WH) / 2, southZ, DW, WH - DH, T));
  // bar counter along the north wall — low (waist height)
  const BAR_H = 0.78, barZ = northZ + 1.0;
  const bar = new THREE.Mesh(new THREE.BoxGeometry(RW * 0.6, BAR_H, 0.7),
    new THREE.MeshStandardMaterial({ map: loadPixelTexture(`${SCENE}/interior/bar.png`) }));
  bar.position.set(cx, BAR_H / 2, barZ); group.add(bar);

  // back-bar shelves on the north wall, lined with cups
  const woodMat = () => new THREE.MeshStandardMaterial({ color: 0x6b4a2e });
  const cupMat = new THREE.MeshStandardMaterial({ color: 0xcdbfa0 });
  const shelfZ = northZ + 0.22, shelfW = RW * 0.62, nCups = 7;
  for (const sy of [1.5, 2.05]) {
    const shelf = new THREE.Mesh(new THREE.BoxGeometry(shelfW, 0.07, 0.26), woodMat());
    shelf.position.set(cx, sy, shelfZ); group.add(shelf);
    for (let i = 0; i < nCups; i++) {
      const cup = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.045, 0.13, 10), cupMat);
      cup.position.set(cx - shelfW / 2 + (i + 0.5) * (shelfW / nCups), sy + 0.1, shelfZ);
      group.add(cup);
    }
  }

  // alcohol dispensary on the bar top — a rack with three inverted bottles (optics)
  const dispX = cx - RW * 0.22;
  const standMat = new THREE.MeshStandardMaterial({ color: 0x3a2a1c });
  const back = new THREE.Mesh(new THREE.BoxGeometry(0.54, 0.4, 0.05), standMat);
  back.position.set(dispX, BAR_H + 0.2, barZ - 0.12); group.add(back);
  const rack = new THREE.Mesh(new THREE.BoxGeometry(0.54, 0.05, 0.18), standMat);
  rack.position.set(dispX, BAR_H + 0.06, barZ - 0.04); group.add(rack);
  const glass = [0xb5832e, 0x6c8f4a, 0x8a9bb0];
  for (let i = 0; i < 3; i++) {
    const bottle = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.052, 0.3, 10),
      new THREE.MeshStandardMaterial({ color: glass[i] }));
    bottle.position.set(dispX - 0.17 + i * 0.17, BAR_H + 0.26, barZ - 0.08); group.add(bottle);
  }
  // warm lighting
  group.add(new THREE.HemisphereLight(0x6a4a26, 0x1a0f06, 0.7));
  const lantern = new THREE.PointLight(0xffcf8c, 14, 16, 2.0);
  lantern.position.set(cx, 2.6, cz); group.add(lantern);

  const bounds: WalkBounds = {
    x0: (cx - RW / 2 + 0.5) * PX, x1: (cx + RW / 2 - 0.5) * PX,
    y0: (northZ + 0.8) * PX, y1: (southZ - 0.4) * PX,
  };
  const blockers: Blocker[] = [
    { x0: (cx - RW / 2) * PX, x1: (cx - DW / 2) * PX, y0: southZ * PX - 8, y1: southZ * PX + 8 },
    { x0: (cx + DW / 2) * PX, x1: (cx + RW / 2) * PX, y0: southZ * PX - 8, y1: southZ * PX + 8 },
    { x0: (cx - RW * 0.3) * PX, x1: (cx + RW * 0.3) * PX, y0: (northZ + 0.6) * PX, y1: (northZ + 1.5) * PX }, // bar
  ];
  return {
    bounds, blockers, colliders,
    door: { x: cx * PX, y: (southZ - 0.7) * PX },     // exit prompt point (inside, by the door)
    spawn: { x: cx * PX, y: (southZ - 1.1) * PX },     // where you land after entering
    // NPC placement (world units): adventurers wander south of the bar; the bartender
    // stands behind the bar (which sits at northZ + 1.0) facing the room.
    npcArea: { x0: cx - RW / 2 + 1.2, z0: northZ + 1.6, x1: cx + RW / 2 - 1.2, z1: southZ - 0.6 },
    barSpot: { x: cx, z: northZ + 0.4 },
  };
}

/** A generic enterable STORE interior (industrial/tech): a textured room with a counter,
 *  display racks, an exit doorway in the south wall, accent lighting, and a shopkeeper spot.
 *  Reused per store with that store's own wall/floor/rack assets + accent colour. */
function buildStoreInterior(
  group: THREE.Group, cx: number, cz: number,
  opts: { wall: string; floor: string; rack?: string; accent: number; w?: number; d?: number; counterTex?: string },
): { bounds: WalkBounds; blockers: Blocker[]; colliders: THREE.Object3D[]; door: { x: number; y: number }; spawn: { x: number; y: number }; npcSpot: { x: number; z: number; face: number } } {
  const RW = opts.w ?? 10, RD = opts.d ?? 7, WH = 3.2, T = 0.3, DW = 1.8, DH = 2.4;
  const southZ = cz + RD / 2, northZ = cz - RD / 2;
  const colliders: THREE.Object3D[] = [];
  const wtex = (): THREE.Texture => tiledTexture(opts.wall, RW / 2.2, WH / 2.2);
  const stex = (): THREE.Texture => tiledTexture(opts.wall, RD / 2.2, WH / 2.2);

  group.add(groundRect(opts.floor, cx, cz, RW, RD, 0.0, 1.6)); // floor
  const ceil = new THREE.Mesh(new THREE.PlaneGeometry(RW, RD), new THREE.MeshStandardMaterial({ color: 0x10141a }));
  ceil.rotation.x = Math.PI / 2; ceil.position.set(cx, WH, cz); group.add(ceil);

  const addWall = (m: THREE.Mesh): void => { group.add(m); colliders.push(m); };
  addWall(wall(wtex(), cx, WH / 2, northZ, RW, WH, T));
  addWall(wall(stex(), cx - RW / 2, WH / 2, cz, T, WH, RD));
  addWall(wall(stex(), cx + RW / 2, WH / 2, cz, T, WH, RD));
  // SOUTH (door) wall — three segments (two jambs + the lintel over the doorway) sharing ONE
  // continuous texture grid: each segment's repeat matches its OWN size and its offset anchors
  // it into the whole wall's grid, so the three boxes read as one wall. (Reusing the full-wall
  // repeat per segment squeezed a whole tiling into each box — the stretched/shrunken-tile
  // stitching artifact.) Offsets are keyed to the segment's RIGHT edge because the interior sees
  // the box's −z face, whose u axis runs mirrored (+x → u 0).
  const segTex = (uw: number, uh: number, rightGap: number, oy: number): THREE.Texture => {
    const t = tiledTexture(opts.wall, uw / 2.2, uh / 2.2);
    t.offset.set((((rightGap / 2.2) % 1) + 1) % 1, (((oy / 2.2) % 1) + 1) % 1);
    return t;
  };
  const jambW = (RW - DW) / 2;
  addWall(wall(segTex(jambW, WH, jambW + DW, 0), cx - DW / 2 - jambW / 2, WH / 2, southZ, jambW, WH, T));
  addWall(wall(segTex(jambW, WH, 0, 0), cx + DW / 2 + jambW / 2, WH / 2, southZ, jambW, WH, T));
  addWall(wall(segTex(DW, WH - DH, jambW, DH), cx, (DH + WH) / 2, southZ, DW, WH - DH, T));

  // service counter — short + thin, set out from the north wall so the shopkeeper has clear
  // floor behind it (otherwise the NPC ends up standing inside the counter).
  const counterMat = opts.counterTex
    ? new THREE.MeshStandardMaterial({ map: loadPixelTexture(opts.counterTex) }) // e.g. the tackle shop's plank+drawer front
    : new THREE.MeshStandardMaterial({ color: 0x2a2f38 });
  const counter = new THREE.Mesh(new THREE.BoxGeometry(RW * 0.5, 0.85, 0.4), counterMat);
  counter.position.set(cx, 0.425, northZ + 1.8); group.add(counter);

  // display racks along the side walls
  if (opts.rack) {
    for (const [rx, rz] of [[cx - RW / 2 + 1.3, cz - 1], [cx - RW / 2 + 1.3, cz + 1.5], [cx + RW / 2 - 1.3, cz - 1], [cx + RW / 2 - 1.3, cz + 1.5]]) {
      const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: loadPixelTexture(opts.rack), transparent: true, alphaTest: 0.5 }));
      s.center.set(0.5, 0); s.scale.set(1.8, 1.8, 1); s.position.set(rx, 0.1, rz); group.add(s);
    }
  }

  group.add(new THREE.HemisphereLight(0x6a7480, 0x14181e, 0.75));
  const accent = new THREE.PointLight(opts.accent, 12, 18, 2.0); accent.position.set(cx, 2.6, cz); group.add(accent);

  return {
    bounds: { x0: (cx - RW / 2 + 0.5) * PX, x1: (cx + RW / 2 - 0.5) * PX, y0: (northZ + 0.8) * PX, y1: (southZ - 0.4) * PX },
    blockers: [
      { x0: (cx - RW / 2) * PX, x1: (cx - DW / 2) * PX, y0: southZ * PX - 8, y1: southZ * PX + 8 },
      { x0: (cx + DW / 2) * PX, x1: (cx + RW / 2) * PX, y0: southZ * PX - 8, y1: southZ * PX + 8 },
      { x0: (cx - RW * 0.2) * PX, x1: (cx + RW * 0.2) * PX, y0: (northZ + 1.55) * PX, y1: (northZ + 2.05) * PX }, // counter (short — walk around the ends)
    ],
    colliders,
    door: { x: cx * PX, y: (southZ - 0.7) * PX },   // exit prompt point
    spawn: { x: cx * PX, y: (southZ - 1.1) * PX },  // arrive just inside the door
    npcSpot: { x: cx, z: northZ + 0.8, face: 0 },   // shopkeeper well behind the counter
  };
}

/** Cyberpunk fit-out for the THREADS store: the back-room CHEST (in the back-left corner, at the shared
 *  storeChestAnchor so the proximity check agrees), a back work TABLE with a pixel SEWING MACHINE,
 *  cyberpunk display RACKS along the side walls, and a futuristic PROJECTOR beaming a floating, spinning
 *  garment HOLOGRAM. Returns the hologram controller (ticked from cityAnimate). */
function decorateThreads(group: THREE.Group, cx: number, cz: number, blockers: Blocker[], colliders: THREE.Object3D[], w: number, d: number): ThreadsHologram {
  const northZ = cz - d / 2;
  const box = (bw: number, bh: number, bd: number, color: number, x: number, y: number, z: number, block = true): THREE.Mesh => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, bd), new THREE.MeshStandardMaterial({ color }));
    m.position.set(x, y, z); group.add(m); colliders.push(m);
    if (block) blockers.push({ x0: (x - bw / 2) * PX, x1: (x + bw / 2) * PX, y0: (z - bd / 2) * PX, y1: (z + bd / 2) * PX });
    return m;
  };
  const sprite = (path: string, x: number, z: number, scale: number, yBase = 0.1): THREE.Sprite => {
    const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: loadPixelTexture(path), transparent: true, alphaTest: 0.5 }));
    s.center.set(0.5, 0); s.scale.set(scale, scale, 1); s.position.set(x, yBase, z); group.add(s); return s;
  };

  // CHEST — wooden brass-banded crate in the back-left corner (shared anchor).
  const a = storeChestAnchor("threads")!;
  const chx = a.x / PX, chz = a.y / PX;
  box(1.0, 0.6, 0.7, 0x6b4a2a, chx, 0.3, chz);
  const lid = new THREE.Mesh(new THREE.BoxGeometry(1.04, 0.18, 0.74), new THREE.MeshStandardMaterial({ color: 0x4a3320 }));
  lid.position.set(chx, 0.69, chz); group.add(lid); colliders.push(lid);
  const band = new THREE.Mesh(new THREE.BoxGeometry(1.06, 0.5, 0.1), new THREE.MeshStandardMaterial({ color: 0xb9892f, metalness: 0.6, roughness: 0.4 }));
  band.position.set(chx, 0.35, chz + 0.33); group.add(band);
  const chestGlow = new THREE.PointLight(0x4cd5ff, 3, 6, 2.0); chestGlow.position.set(chx, 1.4, chz + 0.5); group.add(chestGlow);

  // BACK WORK TABLE (back-right, against the wall) + the freestanding SEWING MACHINE station on the floor
  // at its shared anchor (so the server proximity check agrees).
  const sm = sewingMachineAnchor("threads")!;
  const smx = sm.x / PX, smz = sm.y / PX;
  const tx = smx, tz = northZ + 0.6;
  box(1.8, 0.12, 0.9, 0x2b2f38, tx, 0.92, tz);            // table top
  for (const ox of [-0.7, 0.7]) box(0.14, 0.9, 0.14, 0x1b1e24, tx + ox, 0.45, tz - 0.3, false); // legs
  sprite(`${SCENE}/village/threads/sewing_machine.png`, smx, smz, 1.9, 0.05); // treadle machine at the station anchor

  // CYBERPUNK display RACKS along the side walls (seated a bit lower).
  for (const [rx, rz] of [[cx - w / 2 + 1.1, cz - 0.5], [cx - w / 2 + 1.1, cz + 2.2], [cx + w / 2 - 1.1, cz - 0.5], [cx + w / 2 - 1.1, cz + 2.2]] as const) {
    sprite(`${SCENE}/village/threads/rack_cyber.png`, rx, rz, 2.2, -0.35);
  }

  // PROJECTOR (centre-of-store focal point) + cyan beam + glow + the floating spinning hologram.
  const px = cx, pz = cz;
  box(0.7, 0.5, 0.7, 0x14181f, px, 0.25, pz);             // dark metal base
  const lens = new THREE.Mesh(new THREE.CircleGeometry(0.26, 18), new THREE.MeshBasicMaterial({ color: 0x9af0ff }));
  lens.rotation.x = -Math.PI / 2; lens.position.set(px, 0.51, pz); group.add(lens); // glowing emitter lens (up)
  const beam = new THREE.Mesh(
    new THREE.CylinderGeometry(0.7, 0.08, 1.7, 20, 1, true), // wide at top (the hologram), narrow at the lens
    new THREE.MeshBasicMaterial({ color: 0x5fd6ff, transparent: true, opacity: 0.14, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }),
  );
  beam.position.set(px, 0.5 + 1.7 / 2, pz); beam.renderOrder = 4; group.add(beam);
  const projGlow = new THREE.PointLight(0x6fe0ff, 4, 8, 2.0); projGlow.position.set(px, 1.6, pz); group.add(projGlow);
  return new ThreadsHologram(group, px, pz, 2.1, 1.7);
}

/** Fast-food dressing layered into a store-interior shell: a KITCHEN behind the counter (grill + a
 *  pixel-art COOKING POT + a pixel-art FRIDGE + menu board, warm glow) and a few DINING TABLES with
 *  stools in the customer area. Returns the two interactable props (server px + pick mesh). */
function decorateFood(
  group: THREE.Group, cx: number, cz: number,
  blockers: Blocker[], colliders: THREE.Object3D[], w: number, d: number, bid: string,
): { fridge: { x: number; y: number; obj: THREE.Object3D }; pot: { x: number; y: number; obj: THREE.Object3D }; dispenser: { x: number; y: number; obj: THREE.Object3D } } {
  const northZ = cz - d / 2, southZ = cz + d / 2;
  const potA = cookingPotAnchor(bid), dispA = drinkDispenserAnchor(bid), friA = fridgeAnchor(bid);
  const box = (bw: number, bh: number, bd: number, color: number, x: number, y: number, z: number, block = true): THREE.Mesh => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, bd), new THREE.MeshStandardMaterial({ color }));
    m.position.set(x, y, z); group.add(m); colliders.push(m);
    if (block) blockers.push({ x0: (x - bw / 2) * PX, x1: (x + bw / 2) * PX, y0: (z - bd / 2) * PX, y1: (z + bd / 2) * PX });
    return m;
  };
  // A solid pixel-art BOX prop (6 per-face materials) standing on the floor; collider + blocker + pick mesh.
  const pixBox = (mats: THREE.Material[], bw: number, bh: number, bd: number, x: number, z: number, yBase: number): THREE.Mesh => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(bw, bh, bd), mats);
    m.position.set(x, yBase + bh / 2, z); group.add(m); colliders.push(m);
    blockers.push({ x0: (x - bw / 2) * PX, x1: (x + bw / 2) * PX, y0: (z - bd / 2) * PX, y1: (z + bd / 2) * PX });
    return m;
  };

  // KITCHEN behind the counter (against the north wall): a grill with a glowing ember bed + a self-lit
  // menu board on the wall. The interactable pot (left) + fridge (right) sit at the wall ends.
  box(2.0, 1.0, 0.7, 0x2a2c30, cx - 2.6, 0.5, northZ + 0.5); // grill body
  const grill = new THREE.Mesh(new THREE.PlaneGeometry(1.8, 0.5), new THREE.MeshBasicMaterial({ color: 0xff7a2a }));
  grill.rotation.x = -Math.PI / 2; grill.position.set(cx - 2.6, 1.01, northZ + 0.5); group.add(grill);
  const grillLight = new THREE.PointLight(0xff7a2a, 6, 6, 2.0);
  grillLight.position.set(cx - 2.6, 1.4, northZ + 0.7); group.add(grillLight);
  const menu = new THREE.Mesh(new THREE.PlaneGeometry(3.2, 1.0), new THREE.MeshBasicMaterial({ color: 0x1c140c }));
  menu.position.set(cx, 2.4, northZ + 0.12); group.add(menu);
  const menuGlow = new THREE.Mesh(new THREE.PlaneGeometry(3.0, 0.8), new THREE.MeshBasicMaterial({ color: 0xffcf8a }));
  menuGlow.position.set(cx, 2.4, northZ + 0.13); group.add(menuGlow);

  // COOKING POT — a 3D box pot on a stove block, at the shared anchor (so the server proximity check agrees).
  const potX = potA.x / PX, potZ = potA.y / PX;
  box(0.9, 0.7, 0.6, 0x35251a, potX, 0.35, potZ - 0.05); // wooden stove block under the pot
  const pot = pixBox(makePotMaterials(), 0.72, 0.5, 0.72, potX, potZ, 0.7); // rests on the stove top (y 0.7)
  const potLight = new THREE.PointLight(0xff8a4a, 4, 4, 2.2); potLight.position.set(potX, 1.1, potZ + 0.3); group.add(potLight);

  // FRIDGE — a 3D box fridge at the right end of the kitchen, doors facing the customer (south/+z).
  const friX = friA.x / PX, friZ = friA.y / PX;
  const fridge = pixBox(makeFridgeMaterials(), 0.95, 1.95, 0.72, friX, friZ, 0.0);

  // DRINK DISPENSER — a 3D box machine on the back counter at the kitchen centre (shared anchor).
  const dispX = dispA.x / PX, dispZ = dispA.y / PX;
  box(1.0, 0.85, 0.6, 0x2a2c30, dispX, 0.425, dispZ - 0.05); // counter cabinet under the dispenser
  const dispenser = pixBox(makeDispenserMaterials(), 0.78, 1.0, 0.5, dispX, dispZ, 0.85); // sits on the cabinet
  const dispLight = new THREE.PointLight(0x6ad0ff, 3, 4, 2.2); dispLight.position.set(dispX, 1.7, dispZ + 0.3); group.add(dispLight);

  // DINING TABLES + stools in the customer area (south of the counter), kept clear of the doorway.
  for (const [tx, tz] of [[cx - 3.2, cz + 1.0], [cx + 3.2, cz + 1.0], [cx, cz + 2.6]] as const) {
    box(1.1, 0.12, 1.1, 0xcf6a3a, tx, 0.92, tz);            // table top
    box(0.18, 0.9, 0.18, 0x7a3f22, tx, 0.45, tz, false);   // pedestal (no separate blocker)
    for (const ox of [-0.85, 0.85]) box(0.5, 0.5, 0.5, 0x3a3f47, tx + ox, 0.25, tz, false); // stools
  }

  // Warm fast-food ceiling glow over the dining area.
  const dineGlow = new THREE.PointLight(0xffd9a0, 8, 16, 2.0);
  dineGlow.position.set(cx, 2.8, cz + 1.5); group.add(dineGlow);

  return {
    pot: { x: potA.x, y: potA.y, obj: pot },
    fridge: { x: friA.x, y: friA.y, obj: fridge },
    dispenser: { x: dispA.x, y: dispA.y, obj: dispenser },
  };
}

/** Showroom dressing for OKKA FURNITURE: display beds + safeboxes along the side walls (the same
 *  props players place at home), a brass REGISTER on the counter (the owner collects the takings
 *  there), and a warm showroom glow. Returns the register (server px + pick mesh). */
/** OLD TIMER'S BAITS interior — a cramped one-room tackle shack. Pure VISUAL dressing (no
 *  functionality): the counter register, a tackle workbench, a wicker fish basket, a storage
 *  container (both wrapped in generated pixel-anime textures) and an 8-direction pixel-art fish
 *  BUCKET sprite. Every prop pushes its own blocker (a bit of self collision). Returns the
 *  bucket's per-frame ticker (frame picked by camera yaw — world3d drives it). */
function decorateTackle(
  group: THREE.Group, cx: number, cz: number,
  blockers: Blocker[], colliders: THREE.Object3D[], w: number, d: number,
): { tick: (camYaw: number) => void; board: { x: number; y: number } } {
  const northZ = cz - d / 2;
  const T = `${SCENE}/village/tackle`;
  const block = (x: number, z: number, hx: number, hz: number): void => {
    blockers.push({ x0: (x - hx) * PX, x1: (x + hx) * PX, y0: (z - hz) * PX, y1: (z + hz) * PX });
  };
  const wood = new THREE.MeshStandardMaterial({ map: loadPixelTexture(`${T}/wood.png`), roughness: 0.9 });
  const woodDark = new THREE.MeshStandardMaterial({ color: 0x4a3824, roughness: 0.9 });

  // Cashier register on the (textured) service counter — visual only; sales go through the dialog.
  const register = new THREE.Group();
  const body = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.35, 0.45), new THREE.MeshStandardMaterial({ color: 0x2f333b }));
  body.position.set(0, 0.175, 0); register.add(body);
  const keys = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.1, 0.3), new THREE.MeshStandardMaterial({ color: 0xb9892f, metalness: 0.6, roughness: 0.4 }));
  keys.position.set(0, 0.38, 0.05); keys.rotation.x = -0.35; register.add(keys);
  register.position.set(cx - 1.2, 0.85, northZ + 1.8);
  group.add(register);

  // 8-DIRECTION pixel-art props (PixelLab, campfire-style object pipeline): each is a sprite whose
  // frame follows the camera yaw, so it reads as a solid object from every side.
  const spinTex: THREE.Texture[] = [];
  const prop8 = (sheet: string, x: number, z: number, scale: number, half: number, spin = true): void => {
    const tex = loadPixelTexture(`${T}/${sheet}`);
    tex.repeat.set(1 / 8, 1);
    const spr = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, alphaTest: 0.5 }));
    spr.center.set(0.5, 0);
    spr.scale.set(scale, scale, 1);
    spr.position.set(x, 0.02, z);
    group.add(spr);
    if (half > 0) block(x, z, half, half); // a bit of self collision, like everything else in here
    if (spin) spinTex.push(tex); // spin=false → the south frame shows from every side
  };

  // WALL ART on the north wall: the hung fishing net + the RECORDS SCROLL (the fishing
  // leaderboard — walk up and press E). Flat planes resting on the wall, not billboards.
  const flat = (sheet: string, x: number, y: number, z: number, w2: number, h2: number, rotY = 0): void => {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(w2, h2),
      new THREE.MeshBasicMaterial({ map: loadPixelTexture(`${T}/${sheet}`), transparent: true, alphaTest: 0.5 }));
    m.position.set(x, y, z);
    m.rotation.y = rotY;
    group.add(m);
  };
  flat("net.png", cx - 2.4, 1.9, northZ + 0.18, 1.15, 1.25);
  // the RECORDS SCROLL hangs on the WEST wall, right above the leaning rods
  const scrollX = cx - w / 2 + 0.18, scrollZ = cz - 1.2;
  flat("scroll.png", scrollX, 1.85, scrollZ, 1.15, 1.55, Math.PI / 2);

  // FISHING RODS leaning on the west wall — the same simple 3D rod the character holds
  // (tapered pole + lighter tip + reel disc), tipped back so the top rests on the wall.
  const rod3d = (): THREE.Group => {
    const g = new THREE.Group();
    const main = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.028, 1.35, 5),
      new THREE.MeshStandardMaterial({ color: 0x6b4a2c, roughness: 0.9 }));
    main.position.y = 0.675; g.add(main);
    const tip = new THREE.Mesh(new THREE.CylinderGeometry(0.010, 0.016, 0.45, 5),
      new THREE.MeshStandardMaterial({ color: 0xc8b890, roughness: 0.9 }));
    tip.position.y = 1.35 + 0.225 - 0.02; g.add(tip);
    const reel = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, 0.03, 8),
      new THREE.MeshStandardMaterial({ color: 0x2c2c30 }));
    reel.rotation.x = Math.PI / 2; reel.position.set(0.02, 0.28, 0.05); g.add(reel);
    return g;
  };
  const wallX = cx - w / 2 + 0.16;
  for (const [rz, leanBack, leanSide] of [[cz - 0.3, 0.30, 0.04], [cz + 0.45, 0.24, -0.06]] as const) {
    const rod = rod3d();
    rod.rotation.order = "ZXY";
    rod.rotation.z = leanBack;   // +z-roll tips the top toward -x — onto the west wall
    rod.rotation.x = leanSide;   // small sideways variation between the two
    rod.position.set(wallX + Math.sin(leanBack) * 1.6, 0.02, rz);
    group.add(rod);
  }
  block(cx - w / 2 + 0.35, cz + 0.1, 0.25, 0.8); // slim strip so players don't clip through the rods

  // FISH BASKETS by the east wall — two pixel-art wicker rounds with fish poking out.
  const bkX = cx + w / 2 - 1.0, bkZ = cz - 0.3;
  prop8("basket8.png", bkX, bkZ, 0.7, 0.3, false);
  prop8("basket8.png", bkX - 0.15, bkZ + 0.75, 0.7, 0.3, false); // its twin beside it

  // STORAGE CABINET in the back-east corner — RPG pixel-art cabinet face on a lidded chest.
  const sbX = cx + w / 2 - 1.0, sbZ = northZ + 1.0;
  const crate = new THREE.Mesh(new THREE.BoxGeometry(1.15, 0.85, 0.9), new THREE.MeshStandardMaterial({ map: loadPixelTexture(`${T}/cabinet.png`), roughness: 0.9 }));
  crate.position.set(sbX, 0.425, sbZ); group.add(crate); colliders.push(crate);
  const lid = new THREE.Mesh(new THREE.BoxGeometry(1.23, 0.12, 0.98), woodDark);
  lid.position.set(sbX, 0.88, sbZ); group.add(lid);
  block(sbX, sbZ, 0.65, 0.55);

  // FISH BUCKET by the counter's end.
  prop8("bucket8.png", cx + 1.6, northZ + 1.9, 0.55, 0.3);

  let lastFrame = -1;
  const tick = (camYaw: number): void => {
    // pick the rotation frame the camera should see (8 steps of 45°) — all props share it
    const f = ((Math.round(camYaw / (Math.PI / 4)) % 8) + 8) % 8;
    if (f === lastFrame) return;
    lastFrame = f;
    for (const t of spinTex) t.offset.x = f / 8;
  };

  // A single warm lantern glow — it's a small shack, one light is plenty.
  const glow = new THREE.PointLight(0xffd9a0, 5, 9, 2.0);
  glow.position.set(cx, 2.4, cz); group.add(glow);
  return { tick, board: { x: (scrollX + 0.9) * PX, y: scrollZ * PX } };
}

function decorateFurni(
  group: THREE.Group, cx: number, cz: number,
  blockers: Blocker[], colliders: THREE.Object3D[], w: number, d: number,
): { x: number; y: number; obj: THREE.Object3D } {
  const northZ = cz - d / 2;
  const showcase = (itemId: string, x: number, z: number, ry = 0): void => {
    const obj = furnitureMesh(itemId);
    obj.position.set(x, 0.02, z); obj.rotation.y = ry;
    group.add(obj); colliders.push(obj);
    blockers.push({ x0: (x - 0.9) * PX, x1: (x + 0.9) * PX, y0: (z - 0.9) * PX, y1: (z + 0.9) * PX });
  };
  // Display stock: two made-up beds along the west wall, two safes along the east wall.
  showcase("bed", cx - w / 2 + 1.6, cz - 1.6, Math.PI / 2);
  showcase("bed", cx - w / 2 + 1.6, cz + 1.8, Math.PI / 2);
  showcase("safebox", cx + w / 2 - 1.4, cz - 1.4);
  showcase("safebox", cx + w / 2 - 1.4, cz + 1.2);
  // Low display plinths under the showcase spots read as a showroom floor.
  for (const [px_, pz] of [[cx - w / 2 + 1.6, cz - 1.6], [cx - w / 2 + 1.6, cz + 1.8], [cx + w / 2 - 1.4, cz - 1.4], [cx + w / 2 - 1.4, cz + 1.2]] as const) {
    const plinth = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.06, 2.2), new THREE.MeshStandardMaterial({ color: 0x2b2118 }));
    plinth.position.set(px_, 0.03, pz); group.add(plinth);
  }
  // The counter REGISTER — a brass-trimmed till at the left end of the service counter (the
  // counter sits at northZ + 1.8; see buildStoreInterior). Owner-interactable (E / click).
  const regX = cx - 2.5, regZ = northZ + 1.8;
  const register = new THREE.Group();
  const body = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.35, 0.45), new THREE.MeshStandardMaterial({ color: 0x2f333b }));
  body.position.set(0, 0.175, 0); register.add(body);
  const keys = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.1, 0.3), new THREE.MeshStandardMaterial({ color: 0xb9892f, metalness: 0.6, roughness: 0.4 }));
  keys.position.set(0, 0.38, 0.05); keys.rotation.x = -0.35; register.add(keys);
  const screen = new THREE.Mesh(new THREE.PlaneGeometry(0.34, 0.16), new THREE.MeshBasicMaterial({ color: 0xffd9a0 }));
  screen.position.set(0, 0.32, -0.23); screen.rotation.y = Math.PI; register.add(screen);
  register.position.set(regX, 0.85, regZ); // on the counter top
  group.add(register); colliders.push(register);
  // Warm showroom lighting: a lamp glow over each display wall.
  for (const [lx, lz] of [[cx - w / 2 + 1.8, cz], [cx + w / 2 - 1.8, cz]] as const) {
    const glow = new THREE.PointLight(0xffd9a0, 6, 12, 2.0);
    glow.position.set(lx, 2.6, lz); group.add(glow);
  }
  return { x: regX * PX, y: (regZ + 0.6) * PX, obj: register };
}

/** A pixel canvas → nearest-filtered sRGB texture. */
function pixCanvasTexture(c: HTMLCanvasElement): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  t.magFilter = THREE.NearestFilter; t.minFilter = THREE.NearestFilter; t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
/** Build a lit material whose map is a freshly pixel-drawn canvas face (W×H px). */
function pixFaceMat(W: number, H: number, draw: (r: (x: number, y: number, w: number, h: number, col: string) => void) => void): THREE.Material {
  const c = document.createElement("canvas"); c.width = W; c.height = H;
  const ctx = c.getContext("2d")!; ctx.imageSmoothingEnabled = false;
  draw((x, y, w, h, col) => { ctx.fillStyle = col; ctx.fillRect(x, y, w, h); });
  return new THREE.MeshStandardMaterial({ map: pixCanvasTexture(c), roughness: 0.8, metalness: 0.12 });
}

// BoxGeometry material order: [ +X right, -X left, +Y top, -Y bottom, +Z front, -Z back ].

/** Six pixel-art faces for the steel two-door FRIDGE (front doors face +Z, toward the customer). */
let fridgeMatCache: THREE.Material[] | null = null;
function makeFridgeMaterials(): THREE.Material[] {
  if (fridgeMatCache) return fridgeMatCache;
  // FRONT (W20 H42): two doors split mid-height, vertical handles, a spec badge.
  const front = pixFaceMat(20, 42, (r) => {
    r(0, 0, 20, 42, "#20262c"); r(1, 1, 18, 40, "#c2ccd2");      // outline + steel face
    r(1, 1, 3, 40, "#dfe6ea"); r(16, 1, 3, 40, "#9aa4ab");      // left highlight / right shade
    r(1, 20, 18, 2, "#20262c");                                 // freezer/fridge door split
    r(14, 4, 2, 12, "#566069"); r(14, 24, 2, 14, "#566069");    // door handles
    r(4, 3, 5, 3, "#7fd0e0"); r(5, 4, 1, 1, "#d9f4fb");         // blue badge
  });
  // SIDE (W16 H42): plain steel panel with a soft vertical seam + highlight.
  const fSide = pixFaceMat(16, 42, (r) => {
    r(0, 0, 16, 42, "#20262c"); r(1, 1, 14, 40, "#b4bec5");
    r(1, 1, 3, 40, "#cfd8dd"); r(12, 1, 3, 40, "#8d97a0"); r(8, 1, 1, 40, "#9aa4ab");
  });
  // BACK (W20 H42): steel with a vent grille up top + a compressor box at the base.
  const back = pixFaceMat(20, 42, (r) => {
    r(0, 0, 20, 42, "#20262c"); r(1, 1, 18, 40, "#aeb8bf");
    for (let y = 5; y < 17; y += 2) r(4, y, 12, 1, "#6e767d");  // vent grille
    r(3, 30, 14, 10, "#3a4047");                                // compressor box
  });
  // TOP (W20 H16): steel lid with an inset panel.
  const top = pixFaceMat(20, 16, (r) => {
    r(0, 0, 20, 16, "#20262c"); r(1, 1, 18, 14, "#cdd6db"); r(3, 3, 14, 10, "#b3bdc4");
  });
  const bottom = pixFaceMat(20, 16, (r) => { r(0, 0, 20, 16, "#15191d"); });
  fridgeMatCache = [fSide, fSide, top, bottom, front, back];
  return fridgeMatCache;
}

/** Six pixel-art faces for the black COOKING POT: dark body + rim + handle on the sides, broth on top. */
let potMatCache: THREE.Material[] | null = null;
function makePotMaterials(): THREE.Material[] {
  if (potMatCache) return potMatCache;
  // SIDE (W20 H14): rim band on top, dark belly, a handle nub, edge shading.
  const side = pixFaceMat(20, 14, (r) => {
    r(0, 0, 20, 14, "#15171b"); r(1, 1, 18, 12, "#23262b");     // outline + body
    r(1, 1, 18, 3, "#3a3f47"); r(1, 1, 18, 1, "#4a515a");       // rim band + lip highlight
    r(1, 4, 3, 9, "#34383f"); r(16, 4, 3, 9, "#14161a");        // left highlight / right shade
    r(0, 5, 2, 4, "#3a3f47"); r(18, 5, 2, 4, "#3a3f47");        // handle nubs
  });
  // TOP (W20 H20): rim ring around an orange broth with chunks + bubbles.
  const top = pixFaceMat(20, 20, (r) => {
    r(0, 0, 20, 20, "#15171b"); r(2, 2, 16, 16, "#3a3f47");     // outer ring (rim)
    r(4, 4, 12, 12, "#c8772f");                                 // broth
    r(5, 5, 4, 3, "#e8a04a"); r(12, 9, 3, 3, "#e8a04a");        // broth glints
    r(8, 11, 3, 2, "#7a4326"); r(6, 8, 2, 2, "#9a5a2e");        // meat chunks
  });
  const bottom = pixFaceMat(20, 20, (r) => { r(0, 0, 20, 20, "#0e1013"); r(3, 3, 14, 14, "#1a1c20"); });
  potMatCache = [side, side, top, bottom, side, side];
  return potMatCache;
}

/** Six pixel-art faces for the DRINK DISPENSER: a soda-fountain face (water + soda nozzles) toward +Z. */
let dispMatCache: THREE.Material[] | null = null;
function makeDispenserMaterials(): THREE.Material[] {
  if (dispMatCache) return dispMatCache;
  // FRONT (W20 H26): brand strip up top, a recessed dispense bay with a blue (water) + red (soda) nozzle,
  // selection buttons, and a drip grate at the base.
  const front = pixFaceMat(20, 26, (r) => {
    r(0, 0, 20, 26, "#1a1d22"); r(1, 1, 18, 24, "#3b4149");      // outline + metal body
    r(1, 1, 18, 4, "#2a6f8a"); r(2, 2, 16, 1, "#7fd0e0");        // top brand strip (lit)
    r(3, 7, 14, 11, "#16181c");                                  // dispense bay (dark recess)
    r(6, 8, 2, 5, "#7fd0e0"); r(6, 13, 2, 1, "#cdeef6");         // water nozzle (blue)
    r(12, 8, 2, 5, "#e0584f"); r(12, 13, 2, 1, "#f6b6b1");       // soda nozzle (red)
    r(5, 19, 4, 2, "#7fd0e0"); r(11, 19, 4, 2, "#e0584f");       // selection buttons
    for (let x = 4; x < 16; x += 2) r(x, 22, 1, 2, "#15171b");   // drip grate
  });
  // SIDE (W14 H26): plain brushed panel with a seam + highlight.
  const side = pixFaceMat(14, 26, (r) => {
    r(0, 0, 14, 26, "#1a1d22"); r(1, 1, 12, 24, "#353b42");
    r(1, 1, 3, 24, "#454c54"); r(10, 1, 3, 24, "#23272c");
  });
  // BACK (W20 H26): plain panel + vent slots + a hose box.
  const back = pixFaceMat(20, 26, (r) => {
    r(0, 0, 20, 26, "#1a1d22"); r(1, 1, 18, 24, "#30353b");
    for (let y = 4; y < 14; y += 2) r(4, y, 12, 1, "#1f2429");   // vents
    r(3, 18, 14, 6, "#23272c");                                  // hose box
  });
  // TOP (W20 H12): two drink tanks viewed from above (blue water + red soda).
  const top = pixFaceMat(20, 12, (r) => {
    r(0, 0, 20, 12, "#1a1d22"); r(1, 1, 18, 10, "#3b4149");
    r(3, 3, 6, 6, "#2a6f8a"); r(4, 4, 4, 4, "#7fd0e0");          // water tank
    r(11, 3, 6, 6, "#8a2f2a"); r(12, 4, 4, 4, "#e0584f");        // soda tank
  });
  const bottom = pixFaceMat(20, 12, (r) => { r(0, 0, 20, 12, "#101216"); });
  dispMatCache = [side, side, top, bottom, front, back];
  return dispMatCache;
}

/** Six pixel-art faces for the lobby VENDING MACHINE: a glowing product window (3 shelves of bright
 *  snack/can blocks) over a keypad + coin slot + dispense flap, toward +Z. Same canvas-face technique
 *  as the fridge/dispenser. */
let vendMatCache: THREE.Material[] | null = null;
function makeVendingMaterials(): THREE.Material[] {
  if (vendMatCache) return vendMatCache;
  // FRONT (W24 H46): lit brand strip, big product window with 3 shelves × 4 slots, keypad column,
  // coin slot, and the pickup flap at the base.
  const front = pixFaceMat(24, 46, (r) => {
    r(0, 0, 24, 46, "#14171c"); r(1, 1, 22, 44, "#26436b");         // outline + deep-blue body
    r(2, 2, 20, 4, "#3fa0c8"); r(3, 3, 18, 1, "#bfeaf6");           // lit brand strip
    r(2, 7, 14, 26, "#0e1116"); r(3, 8, 12, 24, "#1a2029");         // product window (recessed, glass)
    const cans = ["#e0584f", "#e8a04a", "#7fd0e0", "#8de89a", "#c98adf", "#f0d05a", "#e0584f", "#7fd0e0", "#8de89a", "#e8a04a", "#f0d05a", "#c98adf"];
    for (let s = 0; s < 3; s++) {                                    // 3 shelves × 4 product slots
      for (let i = 0; i < 4; i++) { r(4 + i * 3, 10 + s * 8, 2, 4, cans[s * 4 + i]); }
      r(3, 15 + s * 8, 12, 1, "#3a4149");                            // shelf lip
    }
    r(17, 8, 4, 6, "#39547c"); r(18, 9, 2, 1, "#bfeaf6");            // keypad + its little display
    for (let y = 16; y < 24; y += 3) r(18, y, 2, 2, "#4a6890");      // keypad buttons
    r(18, 26, 2, 4, "#0e1116");                                      // coin slot
    r(4, 36, 14, 6, "#0e1116"); r(5, 37, 12, 4, "#232a33");          // pickup flap
    r(2, 43, 20, 2, "#101318");                                      // base vent
  });
  // SIDE (W18 H46): plain blue panel with a seam + highlight.
  const side = pixFaceMat(18, 46, (r) => {
    r(0, 0, 18, 46, "#14171c"); r(1, 1, 16, 44, "#223c60");
    r(1, 1, 3, 44, "#2c4a75"); r(14, 1, 3, 44, "#1a2e4a"); r(9, 1, 1, 44, "#1e3453");
  });
  // BACK (W24 H46): vents + a cable box.
  const back = pixFaceMat(24, 46, (r) => {
    r(0, 0, 24, 46, "#14171c"); r(1, 1, 22, 44, "#1d3050");
    for (let y = 6; y < 20; y += 2) r(5, y, 14, 1, "#12203a");
    r(4, 34, 16, 8, "#16233a");
  });
  const top = pixFaceMat(24, 18, (r) => { r(0, 0, 24, 18, "#14171c"); r(1, 1, 22, 16, "#2a4468"); r(3, 3, 18, 12, "#22385a"); });
  const bottom = pixFaceMat(24, 18, (r) => { r(0, 0, 24, 18, "#0e1013"); });
  vendMatCache = [side, side, top, bottom, front, back];
  return vendMatCache;
}

// (aptWallTex / aptConcreteTex — the apartment plaster + concrete pixel tiles — live in
//  villageScene.ts now, next to the tenement's real interiors; imported above for the lobby/halls.)

/** A small self-lit label plate (canvas text, nearest-filtered) — apartment unit numbers + floor
 *  signs. The canvas sizes itself to the TEXT (measured) so long words (ELEVATOR) don't clip. */
function labelPlate(text: string, fg: string, bg: string, w: number, h: number): THREE.Mesh {
  const FONT = "bold 14px monospace";
  const measure = document.createElement("canvas").getContext("2d")!;
  measure.font = FONT;
  const W = Math.max(48, Math.ceil(measure.measureText(text).width) + 12), H = 24;
  const c = document.createElement("canvas"); c.width = W; c.height = H;
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = fg; ctx.font = FONT; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText(text, W / 2, 13);
  return new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ map: pixCanvasTexture(c) }));
}

/** The bare APARTMENT-BLOCK room shell (lobby / stair hall / apartment unit): textured floor + walls +
 *  ceiling with the standard exit doorway split in the SOUTH wall. buildStoreInterior minus the shop
 *  fit-out (no counter/racks/shopkeeper) — apartments ship EMPTY. */
function buildAptShell(
  group: THREE.Group, cx: number, cz: number,
  opts: {
    /** Wall/floor: an asset path, or a procedural pixel-texture factory (repeatX, repeatY). */
    wall: string | ((rx: number, ry: number) => THREE.Texture);
    floor: string | ((rx: number, ry: number) => THREE.Texture);
    accent: number; w: number; d: number;
    /** Ceiling colour (dark by default; the clean apartments use a light one). */
    ceil?: number;
  },
): { bounds: WalkBounds; blockers: Blocker[]; colliders: THREE.Object3D[]; door: { x: number; y: number }; spawn: { x: number; y: number } } {
  const RW = opts.w, RD = opts.d, WH = 3.2, T = 0.3, DW = 1.8, DH = 2.4;
  const southZ = cz + RD / 2, northZ = cz - RD / 2;
  const colliders: THREE.Object3D[] = [];
  const texOf = (src: string | ((rx: number, ry: number) => THREE.Texture), rx: number, ry: number): THREE.Texture =>
    typeof src === "string" ? tiledTexture(src, rx, ry) : src(rx, ry);
  const wtex = (): THREE.Texture => texOf(opts.wall, RW / 2.2, WH / 2.2);
  const stex = (): THREE.Texture => texOf(opts.wall, RD / 2.2, WH / 2.2);

  const floor = new THREE.Mesh(new THREE.PlaneGeometry(RW, RD), new THREE.MeshStandardMaterial({ map: texOf(opts.floor, RW / 1.6, RD / 1.6) }));
  floor.rotation.x = -Math.PI / 2; floor.position.set(cx, 0.0, cz); group.add(floor);
  const ceil = new THREE.Mesh(new THREE.PlaneGeometry(RW, RD), new THREE.MeshStandardMaterial({ color: opts.ceil ?? 0x10141a }));
  ceil.rotation.x = Math.PI / 2; ceil.position.set(cx, WH, cz); group.add(ceil);

  const addWall = (m: THREE.Mesh): void => { group.add(m); colliders.push(m); };
  addWall(wall(wtex(), cx, WH / 2, northZ, RW, WH, T));
  addWall(wall(stex(), cx - RW / 2, WH / 2, cz, T, WH, RD));
  addWall(wall(stex(), cx + RW / 2, WH / 2, cz, T, WH, RD));
  const jambW = (RW - DW) / 2;
  addWall(wall(wtex(), cx - DW / 2 - jambW / 2, WH / 2, southZ, jambW, WH, T));
  addWall(wall(wtex(), cx + DW / 2 + jambW / 2, WH / 2, southZ, jambW, WH, T));
  addWall(wall(wtex(), cx, (DH + WH) / 2, southZ, DW, WH - DH, T));

  group.add(new THREE.HemisphereLight(0x6a7480, 0x14181e, 0.75));
  const accent = new THREE.PointLight(opts.accent, 10, 16, 2.0); accent.position.set(cx, 2.6, cz); group.add(accent);

  return {
    bounds: { x0: (cx - RW / 2 + 0.5) * PX, x1: (cx + RW / 2 - 0.5) * PX, y0: (northZ + 0.8) * PX, y1: (southZ - 0.4) * PX },
    blockers: [
      { x0: (cx - RW / 2) * PX, x1: (cx - DW / 2) * PX, y0: southZ * PX - 8, y1: southZ * PX + 8 },
      { x0: (cx + DW / 2) * PX, x1: (cx + RW / 2) * PX, y0: southZ * PX - 8, y1: southZ * PX + 8 },
    ],
    colliders,
    door: { x: cx * PX, y: (southZ - 0.7) * PX },   // exit prompt point
    spawn: { x: cx * PX, y: (southZ - 1.1) * PX },  // arrive just inside the door
  };
}

/** The lobby ELEVATOR: brushed-steel sliding doors (a pixel canvas face) set in a metal surround on
 *  the north wall, with an ELEVATOR plate + a lit call button. Solid — stand at the doors + E opens
 *  the floor picker (world3d). Returns the call point (server px). */
function aptElevatorProp(
  group: THREE.Group, blockers: Blocker[], colliders: THREE.Object3D[],
  cx: number, northZ: number,
): { x: number; y: number } {
  // surround: a proud metal frame column set against the wall
  const frame = new THREE.Mesh(new THREE.BoxGeometry(2.3, 2.9, 0.22), new THREE.MeshStandardMaterial({ color: 0x8a8d94, metalness: 0.4, roughness: 0.5 }));
  frame.position.set(cx, 1.45, northZ + 0.26); group.add(frame); colliders.push(frame);
  // the sliding DOORS — a pixel-painted face: two steel leaves, centre seam, kick plates, brush lines
  const doors = pixFaceMat(24, 36, (r) => {
    r(0, 0, 24, 36, "#20262c"); r(1, 1, 22, 34, "#aeb8bf");        // outline + steel
    r(11, 1, 2, 34, "#181d22");                                    // centre seam
    r(2, 1, 2, 34, "#c9d2d8"); r(20, 1, 2, 34, "#8d97a0");         // edge highlight / shade
    for (const x of [5, 8, 15, 18]) r(x, 2, 1, 32, "#9aa4ab");     // brushed-metal lines
    r(1, 30, 10, 5, "#7d868e"); r(13, 30, 10, 5, "#7d868e");       // kick plates
  });
  const leaf = new THREE.Mesh(new THREE.PlaneGeometry(1.7, 2.5), doors);
  leaf.position.set(cx, 1.25, northZ + 0.38); group.add(leaf);
  // the ELEVATOR plate above + a lit floor-indicator strip
  const plate = labelPlate("ELEVATOR", "#ffe1b0", "#20262c", 1.4, 0.42);
  plate.position.set(cx, 2.95, northZ + 0.38); group.add(plate);
  const indicator = new THREE.Mesh(new THREE.PlaneGeometry(0.7, 0.16), new THREE.MeshBasicMaterial({ color: 0xe8a04a }));
  indicator.position.set(cx, 2.62, northZ + 0.38); group.add(indicator);
  // call button: a small panel with a glowing dot, right of the doors
  const panel = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.4, 0.1), new THREE.MeshStandardMaterial({ color: 0x565c63 }));
  panel.position.set(cx + 1.45, 1.25, northZ + 0.24); group.add(panel);
  const btn = new THREE.Mesh(new THREE.PlaneGeometry(0.09, 0.09), new THREE.MeshBasicMaterial({ color: 0xffd98a }));
  btn.position.set(cx + 1.45, 1.3, northZ + 0.3); group.add(btn);
  const glow = new THREE.PointLight(0xffe1b0, 2.5, 5, 2.0); glow.position.set(cx, 2.2, northZ + 1.1); group.add(glow);
  blockers.push({ x0: (cx - 1.15) * PX, x1: (cx + 1.15) * PX, y0: northZ * PX, y1: (northZ + 0.6) * PX });
  return { x: cx * PX, y: (northZ + 1.1) * PX }; // stand here + E → floor picker
}

/** The tenement's ground-floor LOBBY: the ELEVATOR centred on the north wall (E → floor picker),
 *  the pixel VENDING MACHINE beside it, and the exit doorway (south) back to the street. */
function buildAptLobby(group: THREE.Group, cx: number, cz: number): {
  bounds: WalkBounds; blockers: Blocker[]; colliders: THREE.Object3D[];
  door: { x: number; y: number }; spawn: { x: number; y: number };
  elevator: { x: number; y: number };
} {
  const RW = 12, RD = 9;
  // Same clean plaster as the upper floors (the raw concrete walls read like a parking garage).
  const shell = buildAptShell(group, cx, cz, { wall: aptWallTex, floor: aptConcreteTex, accent: 0xffd9a0, w: RW, d: RD, ceil: 0xbfb9ad });
  const northZ = cz - RD / 2;
  // ELEVATOR — centred on the north wall, serving floors 2 + 3.
  const elevator = aptElevatorProp(group, shell.blockers, shell.colliders, cx, northZ);
  // VENDING MACHINE — east of the elevator; its +Z product window faces south, into the lobby.
  const vend = new THREE.Mesh(new THREE.BoxGeometry(0.95, 1.9, 0.75), makeVendingMaterials());
  vend.position.set(cx + 4, 0.95, northZ + 0.55); group.add(vend); shell.colliders.push(vend);
  shell.blockers.push({ x0: (cx + 3.45) * PX, x1: (cx + 4.55) * PX, y0: northZ * PX, y1: (northZ + 1.0) * PX });
  const vendGlow = new THREE.PointLight(0x7fd0e0, 3, 5, 2.0); vendGlow.position.set(cx + 4, 1.4, northZ + 1.3); group.add(vendGlow);
  // a worn doormat inside the entrance
  const mat = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 0.9), new THREE.MeshLambertMaterial({ color: 0x4a3b28 }));
  mat.rotation.x = -Math.PI / 2; mat.position.set(cx, 0.015, cz + RD / 2 - 1.0); group.add(mat);
  return { ...shell, elevator };
}

/** An upper-floor stair HALL: the stairwell doorway (south — back down to the lobby) and the floor's
 *  TWO apartment doors on the north wall, each with a lit unit plate. Door portals + locks live in the
 *  controller; each door trigger sits just in front of its panel. */
function buildAptHall(group: THREE.Group, cx: number, cz: number, units: [string, string]): {
  bounds: WalkBounds; blockers: Blocker[]; colliders: THREE.Object3D[];
  door: { x: number; y: number }; spawn: { x: number; y: number };
  doorA: { x: number; y: number }; doorB: { x: number; y: number };
} {
  const RW = 10, RD = 7;
  const shell = buildAptShell(group, cx, cz, { wall: aptWallTex, floor: aptConcreteTex, accent: 0xffe1b0, w: RW, d: RD, ceil: 0xbfb9ad });
  const northZ = cz - RD / 2;
  const doorAt = (x: number, unit: string): { x: number; y: number } => {
    const frame = new THREE.Mesh(new THREE.BoxGeometry(1.5, 2.4, 0.14), new THREE.MeshStandardMaterial({ color: 0x8a8d94 }));
    frame.position.set(x, 1.2, northZ + 0.2); group.add(frame);
    const panel = new THREE.Mesh(new THREE.PlaneGeometry(1.15, 2.15), new THREE.MeshBasicMaterial({ color: 0x140f0a }));
    panel.position.set(x, 1.1, northZ + 0.28); group.add(panel);
    const plate = labelPlate(unit, "#ffe1b0", "#20262c", 0.7, 0.35);
    plate.position.set(x, 2.6, northZ + 0.28); group.add(plate);
    const glow = new THREE.PointLight(0xffd9a0, 1.6, 4, 2.0); glow.position.set(x, 2.2, northZ + 0.9); group.add(glow);
    return { x: x * PX, y: (northZ + 1.0) * PX };
  };
  const doorA = doorAt(cx - 2.5, units[0]);
  const doorB = doorAt(cx + 2.5, units[1]);
  // a hall runner rug between the doors
  const rug = new THREE.Mesh(new THREE.PlaneGeometry(6, 1.4), new THREE.MeshLambertMaterial({ color: 0x4a2f2a }));
  rug.rotation.x = -Math.PI / 2; rug.position.set(cx, 0.015, cz); group.add(rug);
  return { ...shell, doorA, doorB };
}

// (The apartment HOMES are no longer separate far-away rooms — they're built physically into the
//  tenement's upper storeys by villageScene's apartment(), sharing the village world space. Only the
//  lobby + stair halls remain separate zones here.)

/** Bank-specific dressing layered into an already-built bank interior: a glass teller PARTITION
 *  (window + locked staff door) that seals off the area behind the counter, a wall vault + gold,
 *  the upgradable STORAGE locker, and the WEALTH RANKBOARD. Pushes the partition/vault blockers
 *  (so the player can't slip behind the counter) and returns the rankboard + storage interaction
 *  points (server px) — stand near one + E. */
function decorateBank(
  group: THREE.Group, cx: number, cz: number, blockers: Blocker[], colliders: THREE.Object3D[], w = 10, d = 7,
): { rankboard: { x: number; y: number }; storage: { x: number; y: number } } {
  const RW = w, RD = d, WH = 3.2, T = 0.3;
  const northZ = cz - RD / 2, southZ = cz + RD / 2, eastX = cx + RW / 2, westX = cx - RW / 2;
  const wallTex = (w: number): THREE.Texture => tiledTexture(`${SCENE}/village/metal.png`, w / 2.2, WH / 2.2);

  // TELLER PARTITION — a wall on z = northZ+2 that protects everything behind the counter. The
  // counter (centre) is the sill; above it is a glass teller window; the two side bays are solid
  // metal, one carrying a locked staff door. Blockers seal the whole width.
  const partZ = northZ + 2.0;
  const gapHalf = RW * 0.28; // matches the counter's blocker half-width
  for (const [x0, x1] of [[westX, cx - gapHalf], [cx + gapHalf, eastX]] as const) {
    const w = x1 - x0, mid = (x0 + x1) / 2;
    const seg = new THREE.Mesh(new THREE.BoxGeometry(w, WH, T), new THREE.MeshStandardMaterial({ map: wallTex(w) }));
    seg.position.set(mid, WH / 2, partZ); group.add(seg); colliders.push(seg);
    blockers.push({ x0: x0 * PX, x1: x1 * PX, y0: (partZ - 0.3) * PX, y1: (partZ + 0.3) * PX });
  }
  // lintel above the teller window (centre gap)
  const lintel = new THREE.Mesh(new THREE.BoxGeometry(gapHalf * 2, 0.9, T), new THREE.MeshStandardMaterial({ map: wallTex(gapHalf * 2) }));
  lintel.position.set(cx, WH - 0.45, partZ); group.add(lintel);
  // glass teller window (see + click the banker through it; doesn't catch the player or clicks)
  const glass = new THREE.Mesh(new THREE.PlaneGeometry(gapHalf * 2 - 0.2, 1.3),
    new THREE.MeshBasicMaterial({ map: bankPixelTexture(48, 24, drawGlass), transparent: true }));
  glass.position.set(cx, 1.65, partZ - 0.02); glass.raycast = () => {}; group.add(glass);
  for (const mx of [cx - gapHalf + 0.1, cx, cx + gapHalf - 0.1]) { // window mullions
    const bar = new THREE.Mesh(new THREE.BoxGeometry(0.08, 1.3, T + 0.04), new THREE.MeshStandardMaterial({ color: 0x9aa0a8 }));
    bar.position.set(mx, 1.65, partZ); group.add(bar);
  }
  // locked staff door on the left bay (pixel door panel, south face)
  const door = new THREE.Mesh(new THREE.PlaneGeometry(1.1, 2.3),
    new THREE.MeshBasicMaterial({ map: bankPixelTexture(24, 48, drawDoor), transparent: true, alphaTest: 0.4 }));
  door.position.set((westX + cx - gapHalf) / 2, 1.18, partZ - T / 2 - 0.02); door.raycast = () => {}; group.add(door);

  // Vault disc + gold bars, behind the partition (decorative, protected).
  const disc = new THREE.Mesh(new THREE.CylinderGeometry(1.0, 1.0, 0.22, 24),
    new THREE.MeshStandardMaterial({ color: 0x3a4048, metalness: 0.6, roughness: 0.5 }));
  disc.rotation.x = Math.PI / 2; disc.position.set(cx - 3.0, 1.5, northZ + 0.22); group.add(disc);
  const wheel = new THREE.Mesh(new THREE.TorusGeometry(0.4, 0.07, 8, 20), new THREE.MeshStandardMaterial({ color: 0x9aa0a8 }));
  wheel.position.set(cx - 3.0, 1.5, northZ + 0.36); group.add(wheel);
  for (let i = 0; i < 4; i++) {
    const spoke = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.06, 0.06), new THREE.MeshStandardMaterial({ color: 0x9aa0a8 }));
    spoke.position.set(cx - 3.0, 1.5, northZ + 0.36); spoke.rotation.z = (i * Math.PI) / 4; group.add(spoke);
  }
  const goldMat = new THREE.MeshStandardMaterial({ color: 0xcfd2d6, metalness: 0.5, roughness: 0.4 });
  for (const [bx, by] of [[-0.28, 0], [0.28, 0], [0, 0.18]] as const) {
    const bar = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.16, 0.26), goldMat);
    bar.position.set(cx + 3.2 + bx, 0.09 + by, northZ + 0.5); group.add(bar);
  }

  // STORAGE LOCKER — a metal safe against the WEST wall (public side). Stand in front + E to open
  // the upgradable vault. Its pixel-art safe-door faces into the room.
  const lz = cz + 2.5;
  const locker = new THREE.Mesh(new THREE.BoxGeometry(0.5, 1.8, 1.2), new THREE.MeshStandardMaterial({ color: 0x2a2f36 }));
  locker.position.set(westX + 0.25, 0.9, lz); group.add(locker); colliders.push(locker);
  blockers.push({ x0: westX * PX, x1: (westX + 0.6) * PX, y0: (lz - 0.7) * PX, y1: (lz + 0.7) * PX });
  const safeDoor = new THREE.Mesh(new THREE.PlaneGeometry(1.0, 1.5), new THREE.MeshBasicMaterial({ map: bankPixelTexture(28, 42, drawSafe) }));
  safeDoor.rotation.y = Math.PI / 2; safeDoor.position.set(westX + 0.51, 1.0, lz); group.add(safeDoor);
  const keypad = new THREE.PointLight(0xf4f4f4, 3, 4, 2.0); keypad.position.set(westX + 1.1, 1.2, lz); group.add(keypad);

  // WEALTH RANKBOARD — a self-lit pixel screen in a metal frame mounted on the EAST WALL, centred
  // in the guest area. Stand in front of it + E to open the rankings.
  const gz = (northZ + 2.0 + southZ) / 2; // guest-area centre (between the partition and south wall)
  const frame = new THREE.Mesh(new THREE.BoxGeometry(0.16, 2.0, 3.0), new THREE.MeshStandardMaterial({ color: 0x12161c }));
  frame.position.set(eastX - 0.1, 1.9, gz); group.add(frame); colliders.push(frame);
  const screen = new THREE.Mesh(new THREE.PlaneGeometry(2.6, 1.72), new THREE.MeshBasicMaterial({ map: rankboardScreenTexture() }));
  screen.rotation.y = -Math.PI / 2; screen.position.set(eastX - 0.19, 1.94, gz); group.add(screen);
  const glow = new THREE.PointLight(0xf4f4f4, 5, 10, 2.0); glow.position.set(eastX - 1.0, 1.95, gz); group.add(glow);

  return {
    rankboard: { x: (eastX - 1.6) * PX, y: gz * PX }, // stand in front of the wall board
    storage: { x: (westX + 1.6) * PX, y: lz * PX },
  };
}

/** Build a small pixel canvas, draw into it, and return it as a NearestFilter (crisp-pixel) texture. */
function bankPixelTexture(w: number, h: number, draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void): THREE.CanvasTexture {
  const c = document.createElement("canvas"); c.width = w; c.height = h;
  const ctx = c.getContext("2d")!; ctx.imageSmoothingEnabled = false;
  draw(ctx, w, h);
  const tex = new THREE.CanvasTexture(c);
  tex.magFilter = THREE.NearestFilter; tex.minFilter = THREE.NearestFilter; tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Teller-window glass: a faint translucent pane with a gray frame and two light reflection streaks. */
function drawGlass(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = "rgba(150,170,180,0.14)"; ctx.fillRect(0, 0, w, h);     // faint tint
  ctx.fillStyle = "rgba(235,245,250,0.22)";                              // reflections
  for (let i = 0; i < h; i++) { ctx.fillRect(6 + i, i, 2, 1); ctx.fillRect(20 + i, i, 1, 1); }
  ctx.strokeStyle = "#9aa0a8"; ctx.lineWidth = 2; ctx.strokeRect(1, 1, w - 2, h - 2); // frame
}

/** A plain pixel door (gray, two panels + a knob). */
function drawDoor(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = "#3a3f46"; ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = "#1b1e22"; ctx.lineWidth = 2; ctx.strokeRect(2, 1, w - 4, h - 2);
  ctx.fillStyle = "#2c3037"; ctx.fillRect(5, 6, w - 10, h / 2 - 8); ctx.fillRect(5, h / 2 + 2, w - 10, h / 2 - 8);
  ctx.fillStyle = "#c9ccd1"; ctx.fillRect(w - 8, h / 2 - 2, 3, 4); // knob
}

/** A pixel safe door for the storage locker (metal, round dial, handle, rivets, $). */
function drawSafe(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  ctx.fillStyle = "#3a4048"; ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = "#20242a"; ctx.lineWidth = 2; ctx.strokeRect(2, 2, w - 4, h - 4);
  const cxp = w / 2, cyp = h * 0.42;
  ctx.strokeStyle = "#cfd2d6"; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(cxp, cyp, 8, 0, Math.PI * 2); ctx.stroke();
  ctx.fillStyle = "#cfd2d6"; ctx.fillRect(cxp - 1, cyp - 11, 2, 4); ctx.fillRect(cxp - 1, cyp + 7, 2, 4);
  ctx.fillStyle = "#9aa0a8"; ctx.fillRect(cxp - 1, h * 0.72, 2, 8); ctx.fillRect(cxp - 5, h * 0.72, 10, 2); // handle
  ctx.fillStyle = "#23262c"; for (const [rx, ry] of [[5, 5], [w - 6, 5], [5, h - 6], [w - 6, h - 6]]) ctx.fillRect(rx, ry, 2, 2);
}

/** A baked pixel-art texture for the bank's rankboard screen: a black panel with a white frame, a
 *  "WEALTH RANKINGS" header and a few stylised list rows (rank pip · name blocks · amount), all in
 *  black/white/gray. Drawn small + scaled up with NearestFilter so it reads as chunky pixels. */
function rankboardScreenTexture(): THREE.CanvasTexture {
  const W = 168, H = 112;
  const c = document.createElement("canvas"); c.width = W; c.height = H;
  const ctx = c.getContext("2d")!; ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = "#000"; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "#f4f4f4"; ctx.lineWidth = 2; ctx.strokeRect(3, 3, W - 6, H - 6);
  ctx.fillStyle = "#f4f4f4"; ctx.font = "9px monospace"; ctx.textBaseline = "middle"; ctx.textAlign = "center";
  ctx.fillText("WEALTH RANKINGS", W / 2, 16);
  ctx.fillStyle = "#3a3a3a"; ctx.fillRect(10, 25, W - 20, 1);
  for (let i = 0; i < 5; i++) {
    const y = 36 + i * 14;
    ctx.fillStyle = i === 0 ? "#f4f4f4" : "#6a6a6a"; ctx.fillRect(12, y - 3, 6, 6);     // rank pip
    ctx.fillStyle = i === 0 ? "#d8d8d8" : "#555";                                        // name blocks
    ctx.fillRect(24, y - 2, 56 - i * 6, 4);
    ctx.fillStyle = "#9a9a9a"; ctx.textAlign = "right"; ctx.font = "8px monospace";      // amount
    ctx.fillText("$" + (9 - i) + ",###", W - 12, y);
    ctx.textAlign = "center"; ctx.font = "9px monospace";
  }
  const tex = new THREE.CanvasTexture(c);
  tex.magFilter = THREE.NearestFilter; tex.minFilter = THREE.NearestFilter; tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** The CASINO interior: an enlarged brick gambling hall with TWO tables — a blackjack table (west,
 *  five stools) and a Texas Hold'em poker table (east, six stools) — each with its own dealer NPC,
 *  an ATM in the corner, and the host (Roxy) by the door. Players walk to a table and press E to
 *  take a seat. The blackjack table centre MUST match BLACKJACK_TABLE_ANCHOR and the poker felt
 *  centre MUST match POKER_TABLE_ANCHOR in shared/constants.ts. */
function buildCasinoInterior(group: THREE.Group, cx: number, cz: number): {
  bounds: WalkBounds; blockers: Blocker[]; colliders: THREE.Object3D[];
  door: { x: number; y: number }; spawn: { x: number; y: number };
  atm: { x: number; y: number }; npcSpot: { x: number; z: number; face: number };
  seats: { x: number; z: number }[];
  pokerNpcSpot: { x: number; z: number; face: number };
  pokerSeats: { x: number; z: number }[];
} {
  const RW = 22, RD = 14, WH = 3.6, T = 0.3, DW = 1.8, DH = 2.4;
  const southZ = cz + RD / 2, northZ = cz - RD / 2;
  const colliders: THREE.Object3D[] = [];
  const blockers: Blocker[] = [];
  const wtex = (): THREE.Texture => tiledTexture(`${SCENE}/village/brick.png`, RW / 2.4, WH / 2.4);
  const stex = (): THREE.Texture => tiledTexture(`${SCENE}/village/brick.png`, RD / 2.4, WH / 2.4);

  group.add(groundRect(`${SCENE}/tiles/wood.png`, cx, cz, RW, RD, 0.0, 2.4)); // casino floor
  const ceil = new THREE.Mesh(new THREE.PlaneGeometry(RW, RD), new THREE.MeshStandardMaterial({ color: 0x140d12 }));
  ceil.rotation.x = Math.PI / 2; ceil.position.set(cx, WH, cz); group.add(ceil);

  const addWall = (m: THREE.Mesh): void => { group.add(m); colliders.push(m); };
  addWall(wall(wtex(), cx, WH / 2, northZ, RW, WH, T));        // north
  addWall(wall(stex(), cx - RW / 2, WH / 2, cz, T, WH, RD));   // west
  addWall(wall(stex(), cx + RW / 2, WH / 2, cz, T, WH, RD));   // east
  // south wall with a central exit doorway back to the village street
  const jambW = (RW - DW) / 2;
  addWall(wall(wtex(), cx - DW / 2 - jambW / 2, WH / 2, southZ, jambW, WH, T));
  addWall(wall(wtex(), cx + DW / 2 + jambW / 2, WH / 2, southZ, jambW, WH, T));
  addWall(wall(wtex(), cx, (DH + WH) / 2, southZ, DW, WH - DH, T));

  // ---- the blackjack table: a wood base with a proper felt+layout TOP raised to table height
  //      (NOT a decal lying on the floor). ----
  const TW = 4.2, TD = TW * (168 / 240), tableCz = cz - 1.0; // preserve the felt asset's aspect
  // wood base (plinth) — gives the table real height + textured sides; blocks walking onto it.
  const woodTex = loadPixelTexture(`${SCENE}/casino/wood.png`);
  woodTex.wrapS = woodTex.wrapT = THREE.RepeatWrapping; woodTex.repeat.set(3, 1);
  const plinth = new THREE.Mesh(new THREE.BoxGeometry(TW * 0.82, 0.62, TD * 0.7),
    new THREE.MeshStandardMaterial({ map: woodTex }));
  plinth.position.set(cx, 0.31, tableCz); group.add(plinth); // top at y=0.62
  // felt top (rail + green layout) laid ON TOP of the base — the actual playing surface.
  const felt = new THREE.Mesh(new THREE.PlaneGeometry(TW, TD),
    new THREE.MeshBasicMaterial({ map: loadPixelTexture(`${SCENE}/casino/felt.png`), transparent: true, alphaTest: 0.4 }));
  felt.rotation.x = -Math.PI / 2; felt.rotation.z = 0; // flat; layout turned 180° (betting circles to the players)
  felt.position.set(cx, 0.63, tableCz); group.add(felt);
  blockers.push({ x0: (cx - TW * 0.4) * PX, x1: (cx + TW * 0.4) * PX, y0: (tableCz - TD * 0.33) * PX, y1: (tableCz + TD * 0.33) * PX });

  // The dealer is the actual host NPC (Roxy, in a suit) — placed behind the table (north) facing the
  // players via `npcSpot` below; no separate dealer sprite.

  // ---- five pixel-art bar stools along the players' (south) arc, facing the dealer. The
  //      same positions are returned as `seats` so seated players' billboards snap onto them. ----
  const stoolTex = loadPixelTexture(`${SCENE}/casino/stool.png`);
  const seats: { x: number; z: number }[] = [];
  for (let i = 0; i < 5; i++) {
    const a = (-58 + i * 29) * (Math.PI / 180); // fan from -58°..+58°, 0 = straight south
    const sxp = cx + Math.sin(a) * (TW * 0.5);
    const szp = tableCz + Math.cos(a) * (TD / 2 + 0.9);
    const stool = new THREE.Sprite(new THREE.SpriteMaterial({ map: stoolTex, transparent: true, alphaTest: 0.5 }));
    stool.center.set(0.5, 0); stool.scale.set(0.62, 0.62, 1);
    stool.position.set(sxp, 0.02, szp); group.add(stool);
    seats.push({ x: sxp, z: szp }); // billboard sits ON the stool (raised in world3d to perch)
  }

  // ---- the POKER table (Texas Hold'em): east of blackjack, its own felt + base + six stools.
  //      Centre MUST match POKER_TABLE_ANCHOR (world 157,70 → here cx+7, cz). ----
  const pcx = cx + 7, pcz = cz; // poker felt centre
  const PTW = 4.6, PTD = PTW * (168 / 240);
  const pWoodTex = loadPixelTexture(`${SCENE}/casino/wood.png`);
  pWoodTex.wrapS = pWoodTex.wrapT = THREE.RepeatWrapping; pWoodTex.repeat.set(3, 1);
  const pPlinth = new THREE.Mesh(new THREE.BoxGeometry(PTW * 0.82, 0.62, PTD * 0.7),
    new THREE.MeshStandardMaterial({ map: pWoodTex }));
  pPlinth.position.set(pcx, 0.31, pcz); group.add(pPlinth);
  const pFelt = new THREE.Mesh(new THREE.PlaneGeometry(PTW, PTD),
    new THREE.MeshBasicMaterial({ map: loadPixelTexture(`${SCENE}/casino/felt.png`), transparent: true, alphaTest: 0.4 }));
  pFelt.rotation.x = -Math.PI / 2; pFelt.position.set(pcx, 0.63, pcz); group.add(pFelt);
  blockers.push({ x0: (pcx - PTW * 0.4) * PX, x1: (pcx + PTW * 0.4) * PX, y0: (pcz - PTD * 0.33) * PX, y1: (pcz + PTD * 0.33) * PX });
  // six bar stools fanned along the players' (south) arc of the poker table — on a CIRCLE (single
  // radius) so every stool, including the two on the far ends, sits the same distance from the table.
  const pStoolTex = loadPixelTexture(`${SCENE}/casino/stool.png`);
  const pokerSeats: { x: number; z: number }[] = [];
  const pSeatR = PTW * 0.5 + 0.55; // uniform stool radius from the felt centre (clears the oval felt)
  for (let i = 0; i < 6; i++) {
    const a = (-58 + i * (116 / 5)) * (Math.PI / 180); // fan -58°..+58° across 6 seats
    const sxp = pcx + Math.sin(a) * pSeatR;
    const szp = pcz + Math.cos(a) * pSeatR;
    const stool = new THREE.Sprite(new THREE.SpriteMaterial({ map: pStoolTex, transparent: true, alphaTest: 0.5 }));
    stool.center.set(0.5, 0); stool.scale.set(0.62, 0.62, 1);
    stool.position.set(sxp, 0.02, szp); group.add(stool);
    pokerSeats.push({ x: sxp, z: szp });
  }
  const pokerNpcSpot = { x: pcx, z: pcz - PTD / 2 - 0.5, face: 0 }; // poker dealer: behind the felt, facing players

  // lighting: warm pendants over each felt + soft ambient
  group.add(new THREE.HemisphereLight(0x6a5560, 0x141016, 0.7));
  const pend = new THREE.PointLight(0xffcf8a, 16, 16, 2.0); pend.position.set(cx, 2.8, tableCz); group.add(pend);
  const pend2 = new THREE.PointLight(0xffcf8a, 16, 16, 2.0); pend2.position.set(pcx, 2.8, pcz); group.add(pend2);

  // ATM in the south-west corner (same textured kiosk as the village ATM), turned 90° to face the
  // door / room (east, +x) instead of into the south wall.
  const ax = cx - RW / 2 + 1.0, az = southZ - 1.3;
  atmMachine(group, ax, az, Math.PI / 2, colliders, blockers);

  return {
    bounds: { x0: (cx - RW / 2 + 0.6) * PX, x1: (cx + RW / 2 - 0.6) * PX, y0: (northZ + 0.8) * PX, y1: (southZ - 0.6) * PX },
    blockers, colliders,
    door: { x: cx * PX, y: (southZ - 0.7) * PX },   // exit prompt point
    spawn: { x: cx * PX, y: (southZ - 1.3) * PX },  // arrive just inside the door
    atm: { x: ax * PX, y: az * PX },
    npcSpot: { x: cx, z: tableCz - TD / 2 - 0.5, face: 0 }, // the dealer: behind the table, facing the players (south, +z)
    seats,
    pokerNpcSpot,
    pokerSeats,
  };
}

/** Kapon North Way — a wild outdoor zone reached through the city's EAST gate (armed only; the
 *  guard checks). Combat IS allowed here (unlike the green city). A rocky perimeter pens the
 *  player in; a stone return gate at the south edge leads back in through the city's east gate. */
function buildKapon(group: THREE.Group, cx: number, cz: number): {
  bounds: WalkBounds; blockers: Blocker[]; colliders: THREE.Object3D[];
  spawn: { x: number; y: number }; gate: { x: number; y: number }; animate: (dt: number) => void;
} {
  // Match the shared terrain footprint (enlarged ~5×) so cliffs/gate hug the actual ground.
  const HW = KAPON_TERRAIN.hw, HD = KAPON_TERRAIN.hd, T = 1.0, GATE = 5;
  const colliders: THREE.Object3D[] = [];
  const blockers: Blocker[] = [];

  // Lighting + the day/night sky are GLOBAL now (one player-following rig + global lights built in
  // world3d via dayNight.build, shown in every outdoor zone). No per-zone sky/lights here.

  // Octopath-style flat grassy meadow (paved→dirt trail, dense grass plants, groves), lake + river +
  // stone bridge, rocks. Merge its blockers/colliders; its tick scrolls/shimmers the water.
  const terrain = buildKaponTerrain(group, cx, cz);
  blockers.push(...terrain.blockers); colliders.push(...terrain.colliders);

  // No rocky walls on the N/W/E edges — the player is already fenced by `bounds`, and a far pixelated
  // forest + fog (built in buildKaponTerrain) makes those edges read as "the woods continue" rather
  // than a wall. Only the SOUTH side keeps a solid structure (the city gate you came through).
  // SOUTH edge faces the city — build the SAME city gate + walls here (you came in through it),
  // split around the GATE-wide opening (the return gate), instead of the rocky cliff.
  const side = (HW * 2 - GATE) / 2 + T / 2;
  wallSeg(group, colliders, blockers, cx - HW + side / 2, cz + HD, side, T); // south wall (west of gate)
  wallSeg(group, colliders, blockers, cx + HW - side / 2, cz + HD, side, T); // south wall (east of gate)
  const gate = new THREE.Mesh(new THREE.BoxGeometry(GATE, 4.4, T * 0.6),
    new THREE.MeshLambertMaterial({ map: loadPixelTexture(`${SCENE}/village/gate.png`) }));
  gate.position.set(cx, 2.2, cz + HD); group.add(gate); colliders.push(gate);
  blockers.push({ x0: (cx - GATE / 2) * PX, x1: (cx + GATE / 2) * PX, y0: (cz + HD - 0.6) * PX, y1: (cz + HD + 0.6) * PX });

  const animate = (dt: number): void => { terrain.tick(dt); }; // day/night sky is ticked from world3d's loop
  return {
    // North edge is the deep end past the road forks (cave east / forest west); the far tree-line + fog
    // read as "the woods continue". South edge is the city gate you came through.
    bounds: { x0: (cx - HW + 1) * PX, y0: kaponNorthBoundZ * PX, x1: (cx + HW - 1) * PX, y1: (cz + HD - 1) * PX },
    blockers, colliders,
    spawn: { x: cx * PX, y: (cz + HD - 3) * PX },   // arrive just inside the south gate
    gate: { x: cx * PX, y: (cz + HD - 1.5) * PX },  // stand here + E to return to the city
    animate,
  };
}

/** Build the world. Only the SPAWN ISLAND (lamp-lit plaza + tavern interior) is built at boot; the
 *  VILLAGE cluster (village + 6 interiors) and KAPON are each built lazily + independently on first
 *  travel via `ensure(zone)`, so a player who stays in town never loads Kapon (and vice-versa). */
export function buildStreetScene(scene: THREE.Scene): SceneBuild {
  const TAVERN_CX = 10, FRONT_Z = -1.5;
  const INT_CX = 10, INT_CZ = 30;   // tavern interior zone (far north of the plaza)
  const VIL_CX = 70, VIL_CZ = 70;   // village zone (far from both)
  const THR_CX = 110, THR_CZ = 70;  // THREADS store interior zone
  const CAS_CX = 150, CAS_CZ = 70;  // CASINO interior zone (matches BLACKJACK_TABLE_ANCHOR)
  const BNK_CX = 190, BNK_CZ = 70;  // BANK interior zone
  const CTV_CX = 230, CTV_CZ = 70;  // city WINSTON'S tavern interior zone
  const FOO_CX = 270, FOO_CZ = 70;  // FOOD STORE interior zone
  const FUR_CX = 470, FUR_CZ = 70;  // OKKA FURNITURE interior zone (mirrors shared FURNISH_RECTS.furni)
  const TAK_CX = 510, TAK_CZ = 70;  // OLD TIMER'S BAITS interior zone (mirrors shared FURNISH_RECTS.tackle)
  const SMI_CX = 670, SMI_CZ = 70;  // BLACKSMITH interior zone (mirrors shared FURNISH_RECTS.smith)
  // The apartment tenements' SUPPORT interiors (the homes themselves live inside the real buildings):
  const APL_CX = 350, APL_CZ = 70;  // first block: ground-floor LOBBY
  const AH2_CX = 390, AH2_CZ = 70;  // first block: floor-2 stair hall
  const AH3_CX = 430, AH3_CZ = 70;  // first block: floor-3 stair hall
  const ANL_CX = 550, ANL_CZ = 70;  // ANNEX: ground-floor LOBBY
  const AN2_CX = 590, AN2_CZ = 70;  // ANNEX: floor-2 stair hall
  const AN3_CX = 630, AN3_CZ = 70;  // ANNEX: floor-3 stair hall
  const KAP_CX = KAPON_TERRAIN.cx, KAP_CZ = KAPON_TERRAIN.cz;  // Kapon North Way — derive from the terrain so the scene + zone box always track the (expanded) wilds

  const zone = (group: THREE.Group, bounds: WalkBounds, blockers: Blocker[], colliders: THREE.Object3D[], fog: THREE.Fog, bg: number): ZoneData =>
    ({ group, bounds, blockers, colliders, fog, bg });

  // ---- Plaza (tavern exterior) — spawn island ----
  // Outdoor: lit by the GLOBAL day/night lights (dayNight). The tavern's own porch/lamp point-lights
  // still add warm pools at night.
  const plaza = new THREE.Group();
  plaza.add(groundRect(`${SCENE}/tiles/sand.png`, 10, 7.5, 200, 200, 0.0, 1.0));
  plaza.add(groundRect(`${SCENE}/tiles/stone.png`, 10, 3.8, 11, 9, 0.02, 0.45));
  plaza.add(groundRect(`${SCENE}/tiles/wood.png`, 10, -0.6, 5.6, 1.8, 0.04, 0.4));
  const tavern = buildTavern(plaza, TAVERN_CX, FRONT_Z);
  for (const [lx, lz] of [[6.2, 1.2], [13.8, 1.2], [6.2, 7.6], [13.8, 7.6]]) addLamp(plaza, lx, lz);
  scene.add(plaza);

  // ---- Tavern interior — spawn island ----
  const interior = new THREE.Group();
  const int = buildInterior(interior, INT_CX, INT_CZ);
  scene.add(interior);

  const zones: Partial<Record<ZoneId, ZoneData>> = {
    plaza: zone(plaza, { x0: 5.8 * PX, y0: -1.0 * PX, x1: 14.2 * PX, y1: 8.0 * PX }, tavern.blockers, tavern.colliders, new THREE.Fog(0x040405, 8, 30), 0x040405),
    tavern: zone(interior, int.bounds, int.blockers, int.colliders, new THREE.Fog(0x140d08, 6, 26), 0x140d08),
  };

  const portals: Portal[] = [
    { zone: "plaza", to: "tavern", trigger: { x: TAVERN_CX * PX, y: -0.4 * PX }, spawn: int.spawn, label: "enter", lockable: true },
    { zone: "tavern", to: "plaza", trigger: int.door, spawn: { x: TAVERN_CX * PX, y: 0.8 * PX }, label: "exit" },
  ];

  // ---- City + Kapon — each region builds lazily and INDEPENDENTLY on first travel ----
  // Each region pushes its per-frame animator here when it builds (village sky + threads hologram;
  // Kapon wind/water). A list (not one closure) so the village and Kapon can build in either order
  // without clobbering each other.
  // Per-region animators + the zone groups each region added to the scene, tracked so a region can be
  // fully UNLOADED (groups removed + disposed, animator dropped) when the player leaves it.
  let villageAnimator: ((dt: number) => void) | null = null;
  let kaponAnimator: ((dt: number) => void) | null = null;
  let villageGroups: THREE.Group[] = [];
  let kaponGroup: THREE.Group | null = null;
  let villageBuilt = false;
  let kaponBuilt = false;
  const VILLAGE_ZONES: ZoneId[] = [
    "village", "threads", "casino", "bank", "citytavern", "food", "ghosts", "furni", "tackle", "smith",
    "aptlobby", "apthall2", "apthall3", "apt2a", "apt2b", "apt3a", "apt3b",
    "anxlobby", "anxhall2", "anxhall3", "anx2a", "anx2b", "anx3a", "anx3b",
  ];

  // Build the village + its 6 interiors (idempotent). Kept separate from Kapon so entering the city
  // never downloads the wilderness, and vice-versa.
  function ensureVillage(): void {
    if (villageBuilt) return;
    villageBuilt = true;
    const villageGroup = new THREE.Group();
    const vil = buildVillage(villageGroup, VIL_CX, VIL_CZ);
    villageGroup.visible = false;
    scene.add(villageGroup);
    const threadsGroup = new THREE.Group();
    const threads = buildStoreInterior(threadsGroup, THR_CX, THR_CZ, {
      wall: `${SCENE}/village/threads/wall.png`, floor: `${SCENE}/village/threads/floor.png`,
      accent: 0x4cd5ff, w: 14, d: 11,
    });
    const threadsHologram = decorateThreads(threadsGroup, THR_CX, THR_CZ, threads.blockers, threads.colliders, 14, 11);
    threadsGroup.visible = false;
    scene.add(threadsGroup);
    const casinoGroup = new THREE.Group();
    const casino = buildCasinoInterior(casinoGroup, CAS_CX, CAS_CZ);
    casinoGroup.visible = false;
    scene.add(casinoGroup);
    const bankGroup = new THREE.Group();
    const bank = buildStoreInterior(bankGroup, BNK_CX, BNK_CZ, {
      wall: `${SCENE}/village/metal.png`, floor: `${SCENE}/village/concrete.png`, accent: 0x7ad0ff, w: 14, d: 12,
    });
    const bankExtras = decorateBank(bankGroup, BNK_CX, BNK_CZ, bank.blockers, bank.colliders, 14, 12);
    bankGroup.visible = false;
    scene.add(bankGroup);
    const ctavGroup = new THREE.Group();
    const ctav = buildInterior(ctavGroup, CTV_CX, CTV_CZ);
    ctavGroup.visible = false;
    scene.add(ctavGroup);
    const foodGroup = new THREE.Group();
    const food = buildStoreInterior(foodGroup, FOO_CX, FOO_CZ, {
      wall: `${SCENE}/village/food/wall.png`, floor: `${SCENE}/village/food/floor.png`, accent: 0xff9a3a, w: 13, d: 10,
    });
    const foodProps = decorateFood(foodGroup, FOO_CX, FOO_CZ, food.blockers, food.colliders, 13, 10, "food");
    foodGroup.visible = false;
    scene.add(foodGroup);
    // GHOST'S — a second restaurant, the same Watabaga interior at the shared "ghosts" centre.
    const GHO_CX = RESTAURANT_CENTERS.ghosts.x / PX, GHO_CZ = RESTAURANT_CENTERS.ghosts.y / PX;
    const ghostsGroup = new THREE.Group();
    const ghosts = buildStoreInterior(ghostsGroup, GHO_CX, GHO_CZ, {
      wall: `${SCENE}/village/food/wall.png`, floor: `${SCENE}/village/food/floor.png`, accent: 0x9a7aff, w: 13, d: 10,
    });
    const ghostsProps = decorateFood(ghostsGroup, GHO_CX, GHO_CZ, ghosts.blockers, ghosts.colliders, 13, 10, "ghosts");
    ghostsGroup.visible = false;
    scene.add(ghostsGroup);
    // OKKA FURNITURE — the biggest store floor in the city (a proper showroom), warm amber accent.
    const furniGroup = new THREE.Group();
    const furni = buildStoreInterior(furniGroup, FUR_CX, FUR_CZ, {
      wall: `${SCENE}/village/food/wall.png`, floor: `${SCENE}/village/food/floor.png`, accent: 0xffb35c, w: 16, d: 13,
    });
    const furniRegister = decorateFurni(furniGroup, FUR_CX, FUR_CZ, furni.blockers, furni.colliders, 16, 13);
    furniGroup.visible = false;
    scene.add(furniGroup);
    // OLD TIMER'S BAITS — the smallest interior in the city: a one-room fishing-tackle shack.
    const tackleGroup = new THREE.Group();
    const tackle = buildStoreInterior(tackleGroup, TAK_CX, TAK_CZ, {
      wall: `${SCENE}/village/tackle/wood.png`, floor: `${SCENE}/village/food/floor.png`, accent: 0x7ce0a0,
      w: 8, d: 6.5, counterTex: `${SCENE}/village/tackle/counter.png`,
    });
    const tackleProps = decorateTackle(tackleGroup, TAK_CX, TAK_CZ, tackle.blockers, tackle.colliders, 8, 6.5);
    tackleGroup.visible = false;
    scene.add(tackleGroup);
    // BLACKSMITH — Ember's smithy on the residence row: metal walls, ember-orange accent. The
    // furnace + anvil inside are PLACED FURNITURE seeded on the building doc (owned by Ghost),
    // rendered by the FurnitureLayer — so the public smelting station is the real forge station.
    const smithGroup = new THREE.Group();
    const smith = buildStoreInterior(smithGroup, SMI_CX, SMI_CZ, {
      wall: `${SCENE}/village/smith/wall.png`, floor: `${SCENE}/village/smith/floor.png`, accent: 0xff8a3a, w: 10, d: 8,
    });
    // The stone workshop eats the standard store lighting — lift it: a warm hemisphere fill plus
    // an ember pool over the fitted furnace's corner (east of the counter, by the north wall).
    smithGroup.add(new THREE.HemisphereLight(0x9a8f80, 0x2a241e, 0.85));
    const forgeFill = new THREE.PointLight(0xffb36a, 8, 12, 1.8);
    forgeFill.position.set(SMI_CX + 3.2, 2.2, SMI_CZ - 2.2); smithGroup.add(forgeFill);
    smithGroup.visible = false;
    scene.add(smithGroup);
    // APARTMENT TENEMENT support zones — the lobby (vending machine + elevator) and a stair hall per
    // upper floor stay separate far-away rooms. The 4 apartment HOMES are built physically into the
    // tenement's upper storeys by apartment() (vil.aptUnits) — part of the village world itself.
    const lobbyGroup = new THREE.Group();
    const lobby = buildAptLobby(lobbyGroup, APL_CX, APL_CZ);
    lobbyGroup.visible = false; scene.add(lobbyGroup);
    const hall2Group = new THREE.Group();
    const hall2 = buildAptHall(hall2Group, AH2_CX, AH2_CZ, ["2A", "2B"]);
    hall2Group.visible = false; scene.add(hall2Group);
    const hall3Group = new THREE.Group();
    const hall3 = buildAptHall(hall3Group, AH3_CX, AH3_CZ, ["3A", "3B"]);
    hall3Group.visible = false; scene.add(hall3Group);
    // The ANNEX's own support rooms — same builders, its own far-away centres.
    const anxLobbyGroup = new THREE.Group();
    const anxLobby = buildAptLobby(anxLobbyGroup, ANL_CX, ANL_CZ);
    anxLobbyGroup.visible = false; scene.add(anxLobbyGroup);
    const anxHall2Group = new THREE.Group();
    const anxHall2 = buildAptHall(anxHall2Group, AN2_CX, AN2_CZ, ["2A", "2B"]);
    anxHall2Group.visible = false; scene.add(anxHall2Group);
    const anxHall3Group = new THREE.Group();
    const anxHall3 = buildAptHall(anxHall3Group, AN3_CX, AN3_CZ, ["3A", "3B"]);
    anxHall3Group.visible = false; scene.add(anxHall3Group);
    const vilFog = new THREE.Fog(0x2a2e36, 16, 85);
    zones.village = zone(villageGroup, vil.bounds, vil.blockers, vil.colliders, vilFog, 0x2a2e36);
    zones.threads = zone(threadsGroup, threads.bounds, threads.blockers, threads.colliders, new THREE.Fog(0x0a1016, 6, 30), 0x0a1016);
    zones.casino = zone(casinoGroup, casino.bounds, casino.blockers, casino.colliders, new THREE.Fog(0x140d12, 12, 52), 0x140d12);
    zones.bank = zone(bankGroup, bank.bounds, bank.blockers, bank.colliders, new THREE.Fog(0x0a1018, 8, 32), 0x0a1018);
    zones.citytavern = zone(ctavGroup, ctav.bounds, ctav.blockers, ctav.colliders, new THREE.Fog(0x140d08, 6, 26), 0x140d08);
    zones.food = zone(foodGroup, food.bounds, food.blockers, food.colliders, new THREE.Fog(0x1a120a, 7, 30), 0x1a120a);
    zones.ghosts = zone(ghostsGroup, ghosts.bounds, ghosts.blockers, ghosts.colliders, new THREE.Fog(0x120a1a, 7, 30), 0x120a1a);
    zones.furni = zone(furniGroup, furni.bounds, furni.blockers, furni.colliders, new THREE.Fog(0x1a1206, 8, 34), 0x1a1206);
    zones.tackle = zone(tackleGroup, tackle.bounds, tackle.blockers, tackle.colliders, new THREE.Fog(0x120e06, 6, 26), 0x120e06);
    zones.smith = zone(smithGroup, smith.bounds, smith.blockers, smith.colliders, new THREE.Fog(0x160e06, 7, 30), 0x160e06);
    zones.aptlobby = zone(lobbyGroup, lobby.bounds, lobby.blockers, lobby.colliders, new THREE.Fog(0x14100a, 7, 30), 0x14100a);
    zones.apthall2 = zone(hall2Group, hall2.bounds, hall2.blockers, hall2.colliders, new THREE.Fog(0x121014, 6, 26), 0x121014);
    zones.apthall3 = zone(hall3Group, hall3.bounds, hall3.blockers, hall3.colliders, new THREE.Fog(0x121014, 6, 26), 0x121014);
    zones.anxlobby = zone(anxLobbyGroup, anxLobby.bounds, anxLobby.blockers, anxLobby.colliders, new THREE.Fog(0x14100a, 7, 30), 0x14100a);
    zones.anxhall2 = zone(anxHall2Group, anxHall2.bounds, anxHall2.blockers, anxHall2.colliders, new THREE.Fog(0x121014, 6, 26), 0x121014);
    zones.anxhall3 = zone(anxHall3Group, anxHall3.bounds, anxHall3.blockers, anxHall3.colliders, new THREE.Fog(0x121014, 6, 26), 0x121014);
    // The apartment HOMES share the VILLAGE's render world (same group, same fog object — the
    // day/night horizon writes into it): standing inside one, the street outside the open window is
    // simply… there. Their own bounds clamp the player to the room; their colliders are the storey's
    // walls (for the trailing camera).
    const unitOf = new Map(vil.aptUnits.map((u) => [u.unit, u]));
    for (const u of vil.aptUnits) {
      zones[`apt${u.unit}` as ZoneId] = zone(villageGroup, u.bounds, [], u.colliders, vilFog, 0x2a2e36);
    }
    const anxUnitOf = new Map(vil.anxUnits.map((u) => [u.unit, u]));
    for (const u of vil.anxUnits) {
      zones[`anx${u.unit}` as ZoneId] = zone(villageGroup, u.bounds, [], u.colliders, vilFog, 0x2a2e36);
    }
    portals.push(
      { zone: "village", to: "threads", trigger: vil.storeDoors.threads, spawn: threads.spawn, label: "enter THREADS" },
      { zone: "threads", to: "village", trigger: threads.door, spawn: { x: vil.storeDoors.threads.x, y: vil.storeDoors.threads.y + 1.6 * PX }, label: "exit" },
      { zone: "village", to: "casino", trigger: vil.storeDoors.casino, spawn: casino.spawn, label: "enter the CASINO" },
      { zone: "casino", to: "village", trigger: casino.door, spawn: { x: vil.storeDoors.casino.x, y: vil.storeDoors.casino.y - 1.6 * PX }, label: "exit" },
      { zone: "village", to: "bank", trigger: vil.storeDoors.bank, spawn: bank.spawn, label: "enter the BANK" },
      { zone: "bank", to: "village", trigger: bank.door, spawn: { x: vil.storeDoors.bank.x, y: vil.storeDoors.bank.y + 1.6 * PX }, label: "exit" },
      { zone: "village", to: "citytavern", trigger: vil.storeDoors.tavern, spawn: ctav.spawn, label: "enter WINSTON'S" },
      { zone: "citytavern", to: "village", trigger: ctav.door, spawn: { x: vil.storeDoors.tavern.x, y: vil.storeDoors.tavern.y + 1.6 * PX }, label: "exit" },
      { zone: "village", to: "food", trigger: vil.storeDoors.food, spawn: food.spawn, label: "enter WATABAGA" },
      { zone: "food", to: "village", trigger: food.door, spawn: { x: vil.storeDoors.food.x, y: vil.storeDoors.food.y - 1.6 * PX }, label: "exit" },
      { zone: "village", to: "ghosts", trigger: vil.storeDoors.ghosts, spawn: ghosts.spawn, label: "enter GHOST LAB" },
      { zone: "ghosts", to: "village", trigger: ghosts.door, spawn: { x: vil.storeDoors.ghosts.x, y: vil.storeDoors.ghosts.y - 1.6 * PX }, label: "exit" },
      { zone: "village", to: "furni", trigger: vil.storeDoors.furni, spawn: furni.spawn, label: "enter OKKA FURNITURE" },
      // Okka's door faces NORTH (onto the main street, next to the casino) — exit steps north.
      { zone: "furni", to: "village", trigger: furni.door, spawn: { x: vil.storeDoors.furni.x, y: vil.storeDoors.furni.y - 1.6 * PX }, label: "exit" },
      { zone: "village", to: "tackle", trigger: vil.storeDoors.tackle, spawn: tackle.spawn, label: "enter OLD TIMER'S BAITS" },
      { zone: "tackle", to: "village", trigger: tackle.door, spawn: { x: vil.storeDoors.tackle.x, y: vil.storeDoors.tackle.y + 1.6 * PX }, label: "exit" },
      { zone: "village", to: "smith", trigger: vil.storeDoors.smith, spawn: smith.spawn, label: "enter the BLACKSMITH" },
      // The smithy's door faces NORTH (onto the residence row's walkway) — exit steps north.
      { zone: "smith", to: "village", trigger: smith.door, spawn: { x: vil.storeDoors.smith.x, y: vil.storeDoors.smith.y - 1.6 * PX }, label: "exit" },
      // — the apartment tenement: street → lobby → (stairs) floor halls → the 4 unit doors —
      // Unit-door portals target the unit's BUILDING id, so the door lock + FOR-SALE prompt apply.
      { zone: "village", to: "aptlobby", trigger: vil.storeDoors.apartments, spawn: lobby.spawn, label: "enter the APARTMENTS" },
      { zone: "aptlobby", to: "village", trigger: lobby.door, spawn: { x: vil.storeDoors.apartments.x, y: vil.storeDoors.apartments.y + 1.6 * PX }, label: "exit to the street" },
      // (going UP is the lobby ELEVATOR — build.aptElevator + the floor picker, not a door portal)
      { zone: "apthall2", to: "aptlobby", trigger: hall2.door, spawn: { x: lobby.elevator.x, y: lobby.elevator.y + 0.8 * PX }, label: "take the ELEVATOR to the LOBBY" },
      { zone: "apthall3", to: "aptlobby", trigger: hall3.door, spawn: { x: lobby.elevator.x, y: lobby.elevator.y + 0.8 * PX }, label: "take the ELEVATOR to the LOBBY" },
      // Unit doors teleport INTO the real building at that storey's elevation (portal.floor drives
      // the synced Player.floor → render height for everyone).
      { zone: "apthall2", to: "apt2a", trigger: hall2.doorA, spawn: unitOf.get("2a")!.spawn, floor: 2, label: "enter APARTMENT 2A", lockable: true },
      { zone: "apthall2", to: "apt2b", trigger: hall2.doorB, spawn: unitOf.get("2b")!.spawn, floor: 2, label: "enter APARTMENT 2B", lockable: true },
      { zone: "apthall3", to: "apt3a", trigger: hall3.doorA, spawn: unitOf.get("3a")!.spawn, floor: 3, label: "enter APARTMENT 3A", lockable: true },
      { zone: "apthall3", to: "apt3b", trigger: hall3.doorB, spawn: unitOf.get("3b")!.spawn, floor: 3, label: "enter APARTMENT 3B", lockable: true },
      { zone: "apt2a", to: "apthall2", trigger: unitOf.get("2a")!.door, spawn: { x: hall2.doorA.x, y: hall2.doorA.y + 1.0 * PX }, label: "exit" },
      { zone: "apt2b", to: "apthall2", trigger: unitOf.get("2b")!.door, spawn: { x: hall2.doorB.x, y: hall2.doorB.y + 1.0 * PX }, label: "exit" },
      { zone: "apt3a", to: "apthall3", trigger: unitOf.get("3a")!.door, spawn: { x: hall3.doorA.x, y: hall3.doorA.y + 1.0 * PX }, label: "exit" },
      { zone: "apt3b", to: "apthall3", trigger: unitOf.get("3b")!.door, spawn: { x: hall3.doorB.x, y: hall3.doorB.y + 1.0 * PX }, label: "exit" },
      // — the ANNEX: the same street → lobby → halls → units chain, on its own zones —
      { zone: "village", to: "anxlobby", trigger: vil.storeDoors.annex, spawn: anxLobby.spawn, label: "enter the ANNEX" },
      { zone: "anxlobby", to: "village", trigger: anxLobby.door, spawn: { x: vil.storeDoors.annex.x, y: vil.storeDoors.annex.y + 1.6 * PX }, label: "exit to the street" },
      { zone: "anxhall2", to: "anxlobby", trigger: anxHall2.door, spawn: { x: anxLobby.elevator.x, y: anxLobby.elevator.y + 0.8 * PX }, label: "take the ELEVATOR to the LOBBY" },
      { zone: "anxhall3", to: "anxlobby", trigger: anxHall3.door, spawn: { x: anxLobby.elevator.x, y: anxLobby.elevator.y + 0.8 * PX }, label: "take the ELEVATOR to the LOBBY" },
      { zone: "anxhall2", to: "anx2a", trigger: anxHall2.doorA, spawn: anxUnitOf.get("2a")!.spawn, floor: 2, label: "enter ANNEX 2A", lockable: true },
      { zone: "anxhall2", to: "anx2b", trigger: anxHall2.doorB, spawn: anxUnitOf.get("2b")!.spawn, floor: 2, label: "enter ANNEX 2B", lockable: true },
      { zone: "anxhall3", to: "anx3a", trigger: anxHall3.doorA, spawn: anxUnitOf.get("3a")!.spawn, floor: 3, label: "enter ANNEX 3A", lockable: true },
      { zone: "anxhall3", to: "anx3b", trigger: anxHall3.doorB, spawn: anxUnitOf.get("3b")!.spawn, floor: 3, label: "enter ANNEX 3B", lockable: true },
      { zone: "anx2a", to: "anxhall2", trigger: anxUnitOf.get("2a")!.door, spawn: { x: anxHall2.doorA.x, y: anxHall2.doorA.y + 1.0 * PX }, label: "exit" },
      { zone: "anx2b", to: "anxhall2", trigger: anxUnitOf.get("2b")!.door, spawn: { x: anxHall2.doorB.x, y: anxHall2.doorB.y + 1.0 * PX }, label: "exit" },
      { zone: "anx3a", to: "anxhall3", trigger: anxUnitOf.get("3a")!.door, spawn: { x: anxHall3.doorA.x, y: anxHall3.doorA.y + 1.0 * PX }, label: "exit" },
      { zone: "anx3b", to: "anxhall3", trigger: anxUnitOf.get("3b")!.door, spawn: { x: anxHall3.doorB.x, y: anxHall3.doorB.y + 1.0 * PX }, label: "exit" },
    );
    build.villageNpc = vil.npcSpots;
    build.villageNpcArea = vil.npcArea;
    build.threadsNpc = threads.npcSpot;
    build.casinoNpc = casino.npcSpot;
    build.casinoSeats = casino.seats;
    build.pokerNpc = casino.pokerNpcSpot;
    build.pokerSeats = casino.pokerSeats;
    build.bankNpc = bank.npcSpot;
    build.foodNpc = food.npcSpot;
    build.ghostsNpc = ghosts.npcSpot;
    build.furniNpc = furni.npcSpot;
    build.furniRegister = furniRegister;
    build.tackleNpc = tackle.npcSpot;
    build.tackleBucket = tackleProps;
    build.smithNpc = smith.npcSpot;
    build.restaurantProps = { food: foodProps, ghosts: ghostsProps };
    build.rankboard = bankExtras.rankboard;
    build.storage = bankExtras.storage;
    build.cityTavernNpc = { area: ctav.npcArea, bar: ctav.barSpot };
    build.atms = [...vil.atms, casino.atm];
    build.leatherMachine = vil.leatherMachine;
    build.craftingTable = vil.craftingTable;
    build.aptElevators = [
      {
        zone: "aptlobby", x: lobby.elevator.x, y: lobby.elevator.y,
        floors: [
          { label: "Floor 2", zone: "apthall2", spawn: hall2.spawn },
          { label: "Floor 3", zone: "apthall3", spawn: hall3.spawn },
        ],
      },
      {
        zone: "anxlobby", x: anxLobby.elevator.x, y: anxLobby.elevator.y,
        floors: [
          { label: "Floor 2", zone: "anxhall2", spawn: anxHall2.spawn },
          { label: "Floor 3", zone: "anxhall3", spawn: anxHall3.spawn },
        ],
      },
    ];
    // The real homes: occupancy data for world3d (ceiling-light + curtain toggling + relogin-inside).
    build.aptUnits = [
      ...vil.aptUnits.map((u) => ({
        zone: `apt${u.unit}` as ZoneId, floor: u.floor, bounds: u.bounds,
        light: u.light, curtain: u.curtain, windowAnchor: u.windowAnchor,
      })),
      ...vil.anxUnits.map((u) => ({
        zone: `anx${u.unit}` as ZoneId, floor: u.floor, bounds: u.bounds,
        light: u.light, curtain: u.curtain, windowAnchor: u.windowAnchor,
      })),
    ];
    build.aptFootprints = [vil.aptFootprint, vil.anxFootprint];
    villageGroups = [villageGroup, threadsGroup, casinoGroup, bankGroup, ctavGroup, foodGroup, ghostsGroup, furniGroup, tackleGroup, smithGroup, lobbyGroup, hall2Group, hall3Group, anxLobbyGroup, anxHall2Group, anxHall3Group];
    villageAnimator = (dt) => { vil.animateSky(dt); threadsHologram.tick(dt); };
  }

  // Build the Kapon wilderness (idempotent) — only when the player actually ventures in.
  function ensureKapon(): void {
    if (kaponBuilt) return;
    kaponBuilt = true;
    const grp = new THREE.Group();
    const kap = buildKapon(grp, KAP_CX, KAP_CZ);
    grp.visible = false;
    scene.add(grp);
    kaponGroup = grp;
    zones.kapon = zone(grp, kap.bounds, kap.blockers, kap.colliders, new THREE.Fog(0xccdce8, 34, 166), 0xccdce8);
    build.kaponSpawn = kap.spawn;
    // Kapon return — walk to the south gate + E to head back in through the city's east gate.
    portals.push({ zone: "kapon", to: "village", trigger: kap.gate, spawn: { x: (VIL_CX + 20) * PX, y: VIL_CZ * PX }, label: "back to the city" });
    kaponAnimator = (dt) => kap.animate(dt);
  }

  const build: SceneBuild = {
    zones, portals,
    zoneOf: (x, y) => {
      // halfZ defaults to halfX (square box); the village is asymmetric (HW=32, HD=34) — widened for
      // the district remap (nature/residential quarters), capped so it stays clear of the detached
      // THREADS interior box (x ≥ 102) — its halves MUST match villageScene's HW/HD.
      const inBox = (cx: number, cz: number, halfX: number, halfZ = halfX): boolean =>
        x >= (cx - halfX) * PX && x <= (cx + halfX) * PX && y >= (cz - halfZ) * PX && y <= (cz + halfZ) * PX;
      return inBox(INT_CX, INT_CZ, 6) ? "tavern"
        : inBox(THR_CX, THR_CZ, 8) ? "threads"
          : inBox(CAS_CX, CAS_CZ, 13) ? "casino"
            : inBox(BNK_CX, BNK_CZ, 8) ? "bank"
              : inBox(CTV_CX, CTV_CZ, 8) ? "citytavern"
                : inBox(FOO_CX, FOO_CZ, 8) ? "food"
                  : inBox(FUR_CX, FUR_CZ, 9) ? "furni"
                  : inBox(TAK_CX, TAK_CZ, 6) ? "tackle"
                  : inBox(SMI_CX, SMI_CZ, 7) ? "smith"
                  : inBox(RESTAURANT_CENTERS.ghosts.x / PX, RESTAURANT_CENTERS.ghosts.y / PX, 8) ? "ghosts"
                    : inBox(APL_CX, APL_CZ, 8) ? "aptlobby"
                      : inBox(AH2_CX, AH2_CZ, 8) ? "apthall2"
                        : inBox(AH3_CX, AH3_CZ, 8) ? "apthall3"
                        : inBox(ANL_CX, ANL_CZ, 8) ? "anxlobby"
                        : inBox(AN2_CX, AN2_CZ, 8) ? "anxhall2"
                        : inBox(AN3_CX, AN3_CZ, 8) ? "anxhall3"
                          // NOTE: the apartment HOMES occupy the tenement's footprint inside the
                          // village box — zoneOf reports them as "village" (their storeys stack on
                          // the same 2D spot; Player.floor carries the elevation).
                          : inBox(KAP_CX, KAP_CZ, KAPON_TERRAIN.hw, KAPON_TERRAIN.hd) ? "kapon"
                            : inBox(VIL_CX, VIL_CZ, 32, 34) ? "village" : "plaza"; // half-extents MUST match buildVillage HW/HD
    },
    applyZone: (s, z) => {
      const zd = zones[z];
      if (!zd) return;
      s.fog = zd.fog;
      s.background = new THREE.Color(zd.bg);
      // Two passes (hide all, then show the target's group): several zones can SHARE one render
      // group (the apartment homes render the village world), so a single ===-per-id pass would
      // let a later id switch the shared group back off.
      (Object.keys(zones) as ZoneId[]).forEach((id) => { zones[id]!.group.visible = false; });
      zd.group.visible = true;
    },
    animate: (dt, doorsOpen) => { tavern.animate(dt, doorsOpen); villageAnimator?.(dt); kaponAnimator?.(dt); },
    tavernNpc: { area: int.npcArea, bar: int.barSpot },
    ensure: (z) => { if (z === "kapon") ensureKapon(); else if (VILLAGE_ZONES.includes(z)) ensureVillage(); },
    loaded: (z) => (z === "kapon" ? kaponBuilt : VILLAGE_ZONES.includes(z) ? villageBuilt : true),
    // Fully UNLOAD a region: remove + dispose its zone groups (geometry/materials/textures), drop its
    // portals + animator, and null its placement refs, so ensure() rebuilds it fresh on return. The
    // spawn island is always resident (no-op). Caller must have already left the region.
    disposeRegion: (region) => {
      if (region === "city" && villageBuilt) {
        for (const g of villageGroups) { scene.remove(g); disposeObject3D(g); }
        villageGroups = [];
        for (const z of VILLAGE_ZONES) delete zones[z];
        villageAnimator = null;
        villageBuilt = false;
        build.villageNpc = build.villageNpcArea = build.threadsNpc = build.casinoNpc = null;
        build.casinoSeats = build.pokerNpc = build.pokerSeats = build.bankNpc = null;
        build.foodNpc = build.ghostsNpc = build.restaurantProps = build.cityTavernNpc = null;
        build.furniNpc = build.furniRegister = null;
        build.tackleNpc = null;
        build.tackleBucket = null;
        build.smithNpc = null;
        build.atms = build.leatherMachine = build.craftingTable = build.rankboard = build.storage = null;
        build.aptElevators = null;
        build.aptUnits = null;
        build.aptFootprints = null;
      } else if (region === "kapon" && kaponBuilt) {
        if (kaponGroup) { scene.remove(kaponGroup); disposeObject3D(kaponGroup); }
        kaponGroup = null;
        delete zones.kapon;
        kaponAnimator = null;
        kaponBuilt = false;
      }
      // island stays resident.
      // Drop the unloaded region's portals (E-to-enter doors) so they can't be triggered while gone.
      for (let i = portals.length - 1; i >= 0; i -= 1) {
        if (regionOfZone(portals[i].zone) === region) portals.splice(i, 1);
      }
    },
    villageNpc: null,
    villageNpcArea: null,
    threadsNpc: null,
    casinoNpc: null,
    casinoSeats: null,
    pokerNpc: null,
    pokerSeats: null,
    bankNpc: null,
    foodNpc: null,
    ghostsNpc: null,
    furniNpc: null,
    furniRegister: null,
    tackleNpc: null,
    tackleBucket: null,
    smithNpc: null,
    restaurantProps: null,
    cityTavernNpc: null,
    atms: null,
    leatherMachine: null,
    craftingTable: null,
    rankboard: null,
    storage: null,
    aptElevators: null,
    aptUnits: null,
    aptFootprints: null,
    // Known up front (fixed village geometry) so the scroll can target the city before it loads.
    citySpawn: { x: VIL_CX * PX, y: (VIL_CZ + 6) * PX },
    spawnIsland: { x: TAVERN_CX * PX, y: 7 * PX }, // in front of the desert tavern
    // Fixed from the terrain constants, so the east-gate dialog can target Kapon before it's built
    // (must match buildKapon's spawn). ensureKapon overwrites it with the authoritative value.
    kaponSpawn: { x: KAP_CX * PX, y: (KAP_CZ + KAPON_TERRAIN.hd - 3) * PX },
  };
  build.applyZone(scene, "plaza");
  return build;
}
